// src/pages/MaterialEditorPage.js - COMPLETE FIXED VERSION WITH HEADING CHANGE DETECTION
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import VQModal from '../components/VQModal';
import {
  fetchMaterials,
  fetchBreakdownData,
  fetchQuantityCalculatorData,
  updateMaterialData,
  API_BASE_URL,
  fetchRegions,
  fetchMurCategories,
  fetchRValue,
  saveRValue,
  uploadRValueDocument,
  deleteRValue,
  deleteMaterial as apiDeleteMaterial,
  fetchRValueDocuments,
  deleteRValueDocument,
  refreshDocumentUrl,
  fetchQuantityTypes,
  fetchNewMaterialCategories,
  fetchAthenaOc22WorkResults,
  fetchAthenaLineMappings,
  saveAthenaQumurfOc22,
} from '../services/api';
import api from '../services/api';
import './MaterialEditorPage.css';
import QuantityCalculatorModal from './QuantityCalculatorModal';
import CategoryPickerModal from './CategoryPickerModal';
import { logAction } from '../services/api';
import { auth } from '../firebaseConfig';
import { v4 as uuidv4 } from 'uuid';
import { resolveLineItemLinks } from '../utils/quantityLinks';
import axios from 'axios';
import { useRegion } from '../context/RegionContext';
import StyledDropdown from '../components/StyledDropdown';

const SuccessModal = ({ isOpen, onClose, title, message, icon }) => {
  if (!isOpen) return null;

  return (
    <div className="success-modal-overlay" onClick={onClose}>
      <div className="success-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="success-modal-header">
          <div className="success-modal-icon">
            {icon || '✅'}
          </div>
          <h2>{title || 'Success!'}</h2>
        </div>
        <div className="success-modal-body">
          <p>{message}</p>
        </div>
        <div className="success-modal-footer">
          <button className="success-modal-button" onClick={onClose}>
            OK
          </button>
        </div>
      </div>
    </div>
  );
};

const ErrorModal = ({ isOpen, onClose, title, message, icon }) => {
  if (!isOpen) return null;

  return (
    <div className="success-modal-overlay" onClick={onClose}>
      <div className="success-modal-content error-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="success-modal-header error-modal-header">
          <div className="success-modal-icon error-modal-icon">
            {icon || '⚠️'}
          </div>
          <h2>{title || 'Error'}</h2>
        </div>
        <div className="success-modal-body">
          <p style={{ whiteSpace: 'pre-line' }}>{message}</p>
        </div>
        <div className="success-modal-footer">
          <button className="success-modal-button error-modal-button" onClick={onClose}>
            OK
          </button>
        </div>
      </div>
    </div>
  );
};

// ✅ NEW: Validation error helper
const parseValidationError = (error) => {
  if (error.response?.data?.details) {
    const details = error.response.data.details;
    return {
      isValidationError: true,
      fields: details.map(d => ({
        field: d.field,
        message: d.message,
        value: d.value
      }))
    };
  }
  return { isValidationError: false };
};


/* ========= Module-level constants (stable; not React deps) ========= */

const TYPE_BASE = {
  CO2: 'Co2',
  BIO: 'Co2-Biogenic',
  WEIGHT: 'Weight',
  RATE: 'Rate',
};

// R-Value Categories
const RVALUE_CATEGORIES = [
  'Roof',
  'Wall',
  'Floor Slab-on-ground floors',
  'Floors other than slab-on-ground',
  'Windows and doors',
  'Skylights'
];

// DerivativeSystemId → suffix used in MeasurementId
const dsidToSuffix = (dsid) => {
  const map = {
    vquest: '',
    'vquest-au-1': '-AU',
    'vquest-us-1': '-US',
    'vquest-uk-ICE': '-UK',
    'vquest-irl-1': '-IRL',
    'vquest-midrise': '-Midrise',
  };
  return map[dsid] ?? '';
};

const normalizeRegionToDSID = (r) => {
  const map = {
    nz: 'vquest',
    au: 'vquest-au-1',
    us: 'vquest-us-1',
    irl: 'vquest-irl-1',
    uk: 'vquest-uk-ICE',
    mr: 'vquest-midrise',
    vquest: 'vquest',
    'vquest-au-1': 'vquest-au-1',
    'vquest-us-1': 'vquest-us-1',
    'vquest-irl-1': 'vquest-irl-1',
    'vquest-uk-ICE': 'vquest-uk-ICE',
    'vquest-midrise': 'vquest-midrise',
  };
  return map[r] || r || 'vquest';
};

// ✅ Module-level: Rate limit bypass token (persists across renders)
let bypassToken = null;

function MaterialEditorPage() {
  const location = useLocation();
  const preselectedMaterial = location.state?.materialId || '';

  // R-Values state
  const [rValueData, setRValueData] = useState(null);
  const [rValueFiles, setRValueFiles] = useState([]);
  const [uploadingRValue] = useState(false);
  const [dragActiveRValue, setDragActiveRValue] = useState(false);
  const [rValueModalOpen, setRValueModalOpen] = useState(false);
  const [rValueCategory, setRValueCategory] = useState('');
  const [editingCategory, setEditingCategory] = useState(false);

  // ✅ Rate limit bypass states
  const [showBypassModal, setShowBypassModal] = useState(false);
  const [bypassPassword, setBypassPassword] = useState('');
  const [bypassError, setBypassError] = useState('');
  const [bypassLoading, setBypassLoading] = useState(false);
  const [pendingRetry, setPendingRetry] = useState(null);

  // Add these near the top of your component with other state
  const [biogenicCollapsed, setBiogenicCollapsed] = useState(true);
  const [weightCollapsed, setWeightCollapsed] = useState(true);

  const [searchParams] = useSearchParams();
  const materialId = location.state?.materialId || searchParams.get('materialId');

  const [errorModal, setErrorModal] = useState({
    open: false,
    title: '',
    message: '',
    icon: '⚠️',
  });

  const showErrorModal = (title, message, icon = '⚠️') => {
    setErrorModal({ open: true, title, message, icon });
  };


  // Branded success modal
  const [modal, setModal] = useState({
    open: false,
    title: '',
    message: '',
    icon: '✅',
  });
  const showModal = (title, message, icon = '✅') =>
    setModal({ open: true, title, message, icon });

  // Shim window.alert() to our modal
  useEffect(() => {
    const originalAlert = window.alert;
    window.alert = (msg) => {
      try {
        showModal('Notice', String(msg ?? ''), 'ℹ️');
      } catch {
        originalAlert(msg);
      }
    };
    return () => {
      window.alert = originalAlert;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ✅ Axios interceptor to attach bypass token to all requests
  useEffect(() => {
    const requestInterceptor = api.interceptors.request.use((config) => {
      if (bypassToken) {
        config.headers['X-Rate-Limit-Bypass'] = bypassToken;
      }
      return config;
    });

    return () => {
      api.interceptors.request.eject(requestInterceptor);
    };
  }, []);

  const { selectedRegion } = useRegion();

  const [availableRegions, setAvailableRegions] = useState([]);
  const regionName = useMemo(() => {
    const region = availableRegions.find((r) => r.id === selectedRegion);
    return region ? region.name : selectedRegion;
  }, [availableRegions, selectedRegion]);

  const [materials, setMaterials] = useState([]);
  const [filteredMaterials, setFilteredMaterials] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMaterial, setSelectedMaterial] = useState(preselectedMaterial);

  // ── Athena OC22 authoring (USA-Canada only) ──────────────────────────────────────────
  const isAthenaRegion = selectedRegion === 'vquest-us-1';
  // OC22 work-result tree in CategoryPickerModal shape { id, name, ParentCategoryId, Priority }.
  const [oc22Tree, setOc22Tree] = useState([]);
  const [athenaLineMappings, setAthenaLineMappings] = useState({}); // factorId -> {flow, flowTypeOc23, oc22Id, oc22Label}

  // Load the OC22 work-result tree once for the US region.
  useEffect(() => {
    if (!isAthenaRegion) { setOc22Tree([]); return; }
    (async () => {
      try {
        const rows = await fetchAthenaOc22WorkResults();
        setOc22Tree((rows || []).map((r) => ({
          id: r.Id, name: r.full_label, ParentCategoryId: r.parent_id, Priority: r.code,
        })));
      } catch (e) {
        console.error('❌ Error loading OC22 work results:', e);
        setOc22Tree([]);
      }
    })();
  }, [isAthenaRegion]);

  // Load this assembly's per-line flow + OC22 mappings (keyed by QUMURF FactorId).
  useEffect(() => {
    if (!isAthenaRegion || !selectedMaterial) { setAthenaLineMappings({}); return; }
    (async () => {
      try {
        const rows = await fetchAthenaLineMappings(selectedMaterial);
        const map = {};
        (rows || []).forEach((r) => { if (r.factorId) map[r.factorId] = r; });
        setAthenaLineMappings(map);
      } catch (e) {
        console.error('❌ Error loading Athena line mappings:', e);
        setAthenaLineMappings({});
      }
    })();
  }, [isAthenaRegion, selectedMaterial]);

  const [carbonLineItems, setCarbonLineItems] = useState([]);
  const [biogenicLineItems, setBiogenicLineItems] = useState([]);
  const [weightLineItems, setWeightLineItems] = useState([]);
  const [costLineItems, setCostLineItems] = useState([]);

  const [quantityTypes, setQuantityTypes] = useState([]);

  const [editingLineItems, setEditingLineItems] = useState({});

  // Region-scoped lookups
  const [carbonCategories, setCarbonCategories] = useState([]);
  const [biogenicCategories, setBiogenicCategories] = useState([]);
  const [weightCategories, setWeightCategories] = useState([]);
  const [costCategories, setCostCategories] = useState([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [activeLineItem, setActiveLineItem] = useState(null);
  const [rowHistory, setRowHistory] = useState({});
  const [originalData, setOriginalData] = useState([]);
  const [showComparisonModal, setShowComparisonModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Edit Name functionality
  const [showEditNameModal, setShowEditNameModal] = useState(false);
  const [newMaterialName, setNewMaterialName] = useState('');
  const [originalMaterialName, setOriginalMaterialName] = useState('');

  // Edit Description functionality
  const [showEditDescriptionModal, setShowEditDescriptionModal] = useState(false);
  const [newMaterialDescription, setNewMaterialDescription] = useState('');
  const [originalMaterialDescription, setOriginalMaterialDescription] = useState('');

  // Edit Category functionality — reuses the building-element CategoryPickerModal
  const [showEditCategoryModal, setShowEditCategoryModal] = useState(false);
  const [newMaterialCategories, setNewMaterialCategories] = useState([]);
  const [originalMaterialCategoryId, setOriginalMaterialCategoryId] = useState('');

  // Visible (non-hidden) building-element categories for the picker
  const visibleMaterialCategories = useMemo(
    () => (Array.isArray(newMaterialCategories) ? newMaterialCategories.filter((c) => !c.IsHidden) : []),
    [newMaterialCategories],
  );

  // ✅ NEW: Helper to lookup quantity type name by ID
  const getQuantityTypeName = useCallback((quantityTypeId) => {
    if (!quantityTypeId) return '';
    const qt = quantityTypes.find(q => String(q.Id) === String(quantityTypeId));
    return qt ? qt.Name : '';
  }, [quantityTypes]);

  // Load building-element categories for the "Edit Category" picker (same source
  // the New Material / Build Composite page uses).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cats = await fetchNewMaterialCategories();
        if (!cancelled) setNewMaterialCategories(Array.isArray(cats) ? cats : []);
      } catch (e) {
        console.error('❌ Error loading building-element categories:', e);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedRegion]);

  // ✅ Check if error is a rate limit and show bypass modal
  const handleRateLimitError = (error, retryFn) => {
    const status = error?.response?.status || error?.status;
    if (status === 429) {
      setPendingRetry(() => retryFn);
      setShowBypassModal(true);
      return true;
    }
    return false;
  };

  // ✅ Handle rate limit bypass
  const handleBypass = async () => {
    if (!bypassPassword.trim()) {
      setBypassError('Please enter your password.');
      return;
    }

    setBypassLoading(true);
    setBypassError('');

    try {
      const token = await auth.currentUser?.getIdToken();

      const res = await fetch(`${API_BASE_URL}/admin/bypass-rate-limit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ password: bypassPassword }),
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        setBypassError(data.error || 'Invalid password');
        return;
      }

      bypassToken = data.bypassToken;
      console.log('✅ Rate limit bypass granted');

      setShowBypassModal(false);
      setBypassPassword('');
      setBypassError('');

      if (pendingRetry) {
        pendingRetry();
        setPendingRetry(null);
      }
    } catch (e) {
      console.error('❌ Bypass error:', e);
      setBypassError('Failed to verify password. Please try again.');
    } finally {
      setBypassLoading(false);
    }
  };

  // Compute the exact MeasurementId for this region + base
  const measurementIdFor = useCallback(
    (base) => {
      const dsid = normalizeRegionToDSID(selectedRegion);
      const suffix = dsidToSuffix(dsid);
      return `vquest-${base}${suffix}`;
    },
    [selectedRegion]
  );

  // Load region names
  useEffect(() => {
    (async () => {
      try {
        const regions = await fetchRegions();
        setAvailableRegions(regions);
      } catch (e) {
        console.error('Error loading regions:', e);
      }
    })();
  }, []);

  // Keep LS in sync for APIs that read it
  useEffect(() => {
    if (selectedRegion) localStorage.setItem('selectedRegion', selectedRegion);
  }, [selectedRegion]);

  const loadMaterials = useCallback(async () => {
    if (!selectedRegion) return;
    try {
      setLoading(true);
      const response = await fetchMaterials(selectedRegion);
      const list = response.data || [];
      setMaterials(list);
      setFilteredMaterials(list);
    } catch (err) {
      console.error('Error loading materials:', err);
      // ✅ Check for rate limit
      if (handleRateLimitError(err, loadMaterials)) return;
      showErrorModal('Failed to load materials: ' + (err?.message || String(err)));
    } finally {
      setLoading(false);
    }
  }, [selectedRegion]);

  // Load region-scoped categories
  const loadCategories = useCallback(async () => {
    try {
      const [co2, bio, weight, rate, qTypes] = await Promise.all([
        fetchMurCategories(measurementIdFor(TYPE_BASE.CO2)),
        fetchMurCategories(measurementIdFor(TYPE_BASE.BIO)),
        fetchMurCategories(measurementIdFor(TYPE_BASE.WEIGHT)),
        fetchMurCategories(measurementIdFor(TYPE_BASE.RATE)),
        fetchQuantityTypes(selectedRegion),
      ]);
      setCarbonCategories(co2.data || []);
      setBiogenicCategories(bio.data || []);
      setWeightCategories(weight.data || []);
      setCostCategories(rate.data || []);

      // ✅ NEW: Set quantity types
      if (Array.isArray(qTypes)) {
        setQuantityTypes(qTypes);
      } else if (qTypes && Array.isArray(qTypes.data)) {
        setQuantityTypes(qTypes.data);
      } else {
        console.warn('Quantity types data is not an array:', qTypes);
        setQuantityTypes([]);
      }
      
      console.log('✅ Loaded quantity types:', qTypes?.length || qTypes?.data?.length || 0);
    } catch (err) {
      console.error('Error loading categories (region-scoped):', err);
      // ✅ Check for rate limit
      if (handleRateLimitError(err, loadCategories)) return;
      showErrorModal('Failed to load categories: ' + (err?.message || String(err)));
    }
  }, [measurementIdFor, selectedRegion]);

  // Find Category by name inside the provided pool
  const categoryForDesc = (desc, pool) => {
    const hit = (pool || []).find((c) => c.MeasurementUnitName === desc);
    return hit?.Category || '';
  };


// 🆕 Handle document deletion
const handleDeleteDocument = async (documentId) => {
  if (!rValueData?.Id) return;
  
  // Ask for confirmation
  if (!window.confirm('Are you sure you want to delete this document?')) {
    return;
  }
  
  try {
    console.log('🗑️ Deleting document:', documentId);
    const response = await deleteRValueDocument(rValueData.Id, documentId);
    
    if (response.success) {
      console.log('✅ Document deleted successfully');
      
      // Remove from state
      setRValueFiles(prevFiles => prevFiles.filter(file => file.id !== documentId));
      
      // Show success message
      alert('Document deleted successfully');
    }
  } catch (error) {
    console.error('❌ Error deleting document:', error);
    alert(`Failed to delete document: ${error.message}`);
  }
};



  // Sync category when rValueData changes
  useEffect(() => {
    if (rValueData?.category !== undefined) {
      setRValueCategory(rValueData.category || '');
      console.log('🔄 Synced category from rValueData:', rValueData.category);
    }
  }, [rValueData]);

  // ✅ CONSOLIDATED: Load all data in one pass, insert headings once
// ✅ FIXED: Load all data in one pass, fetch R-Value properly
const loadBreakdownData = useCallback(
  async (materialId) => {
    if (!selectedRegion || !materialId) return;
    try {
      setLoading(true);

      // 1️⃣ Fetch breakdown data
      const breakdownData = await fetchBreakdownData(materialId);
      console.log('🔍 Breakdown response:', breakdownData);
      const responseData = breakdownData.data?.data || breakdownData.data || breakdownData || [];
      const data = Array.isArray(responseData) ? responseData : [];
      console.log('✅ Final data array:', data.length, 'items');
      
      setOriginalData(data.map((item) => ({ ...item })));

      // 2️⃣ Fetch calculator data
      const quantityCalculatorData = await fetchQuantityCalculatorData(materialId);
      console.log('🔍 Quantity calculator response:', {
        hasData: !!quantityCalculatorData,
        fullResponse: quantityCalculatorData
      });

      const lineItemsData = Array.isArray(quantityCalculatorData?.data?.data?.lineItems)
        ? quantityCalculatorData.data.data.lineItems
        : [];
      console.log('✅ Extracted lineItemsData:', lineItemsData.length);

      // 3️⃣ 🆕 Fetch R-Value data USING materialId (not rValueData.Id)
      try {
        console.log('🔍 Loading R-Value for material:', materialId);
        const rValueResponse = await fetchRValue(materialId);
        
        if (rValueResponse.success && rValueResponse.data) {
          const rValue = rValueResponse.data;
          console.log('✅ R-Value loaded:', rValue);
          setRValueData(rValue);
          setRValueCategory(rValue.category || '');
          
          // 4️⃣ 🆕 Fetch R-Value documents using the R-Value ID
          try {
            console.log('🔍 Fetching documents for R-Value ID:', rValue.Id);
            const documentsResponse = await fetchRValueDocuments(rValue.Id);
            
            if (documentsResponse.success && documentsResponse.data) {
              console.log(`✅ Loaded ${documentsResponse.data.length} R-Value documents`);
              setRValueFiles(documentsResponse.data);
            } else {
              console.log('⚠️ No R-Value documents found');
              setRValueFiles([]);
            }
          } catch (docError) {
            console.error('❌ Error fetching R-Value documents:', docError);
            setRValueFiles([]);
          }
        } else {
          console.log('⚠️ No R-Value found for this material');
          setRValueData(null);
          setRValueFiles([]);
          setRValueCategory('');
        }
      } catch (rValueError) {
        console.error('❌ Error loading R-Value:', rValueError);
        setRValueData(null);
        setRValueFiles([]);
        setRValueCategory('');
      }

      // 5️⃣ Build rowHistory
      const updatedRowHistory = {};
      console.log('📊 Loading quantity calculator data:', lineItemsData.length, 'items');
      lineItemsData.forEach((lineItem) => {
        console.log(`🔍 Processing line item ${lineItem.id}:`, {
          factor: lineItem.factor,
          measurement_unit_rate_id: lineItem.measurement_unit_rate_id,
          hasRows: !!lineItem.line_item_rows
        });
        
        if (lineItem.id && lineItem.line_item_rows) {
          try {
            updatedRowHistory[lineItem.id] = JSON.parse(lineItem.line_item_rows);
            console.log(`✅ Loaded calculator data for ${lineItem.id}:`, {
              factor: lineItem.factor,
              rowsCount: JSON.parse(lineItem.line_item_rows).length
            });
          } catch (err) {
            console.error('Error parsing line_item_rows:', err);
          }
        }
      });

      // 6️⃣ Region-specific MeasurementIds
      const midCO2 = measurementIdFor(TYPE_BASE.CO2);
      const midBIO = measurementIdFor(TYPE_BASE.BIO);
      const midWEI = measurementIdFor(TYPE_BASE.WEIGHT);
      const midRATE = measurementIdFor(TYPE_BASE.RATE);

      // 7️⃣ Enrich line items
      const enrich = (items, pool, expectedMid) =>
        (items || [])
          .filter((i) => String(i.MeasurementType || '') === expectedMid)
          .map((item) => {
            const li = lineItemsData.find((x) => x.id === item.FactorId);
            const desc = item.CostMeasurementName || '';
            const heading = li?.headings || '';
            const sortOrder = li?.sort_order !== undefined ? li.sort_order : 9999;

            const measurementType = li?.measurement_type || item.MeasurementType || expectedMid;
            
            let finalFactor = parseFloat(item.Factor) || 0;
            
            if (li && li.factor !== undefined && li.factor !== null) {
              const hasCalculatorRows = li.line_item_rows && 
                                        li.line_item_rows !== '[]' && 
                                        JSON.parse(li.line_item_rows || '[]').length > 0;
              
              if (hasCalculatorRows) {
                const calcFactor = parseFloat(li.factor) || 0;
                finalFactor = calcFactor;
              }
            }
            
            // ✅ DEBUG: Log quantity type lookup
            console.log('🔍 Breakdown item:', {
              FactorId: item.FactorId,
              Code: item.MeasurementUnitRateId,
              QuantityTypeId: item.QuantityTypeId,
              QuantityTypeName_Original: item.QuantityTypeName
            });
            
            // ✅ NEW: Lookup quantity type name using the ID
            const quantityTypeName = getQuantityTypeName(item.QuantityTypeId);
            
            // ✅ DEBUG: Log lookup result
            console.log('   → Looked up:', quantityTypeName, 'from ID:', item.QuantityTypeId);
            
            return {
              ...item,
              MeasurementId: measurementType,
              MeasurementType: measurementType,
              Code: item.MeasurementUnitRateId || '',
              Factor: finalFactor,
              LineItemRows: li ? JSON.parse(li.line_item_rows || '[]') : [],
              heading:    li?.headings    || '',   // parent heading label
              subHeading: li?.sub_heading || '',   // 🆕 parent sub-heading label
              sortOrder: sortOrder,
              isHeading:    false,
              isSubHeading: false,                 // 🆕
              Category: categoryForDesc(desc, pool),
              QuantityTypeName: item.QuantityTypeName_Original || item.QuantityTypeName || '',
              TotalResult: finalFactor * (parseFloat(item.Value) || 0),
            };
          });


      const enrichedCarbon = enrich(data, carbonCategories, midCO2);
      const enrichedBiogenic = enrich(data, biogenicCategories, midBIO);
      const enrichedWeight = enrich(data, weightCategories, midWEI);
      const enrichedCost = enrich(data, costCategories, midRATE);

      const sortBySortOrder = (a, b) => (a.sortOrder || 0) - (b.sortOrder || 0);

      enrichedCarbon.sort(sortBySortOrder);
      enrichedBiogenic.sort(sortBySortOrder);
      enrichedWeight.sort(sortBySortOrder);
      enrichedCost.sort(sortBySortOrder);

      console.log('✅ Sorted enrichedCarbon by sort_order:', enrichedCarbon.map(i => ({ 
        id: i.FactorId, 
        heading: i.heading, 
        sortOrder: i.sortOrder 
      })));

      // 8️⃣ Insert heading rows
      const insertHeadingRowsIfNeeded = (enrichedItems) => {
        const hasStructureRows = enrichedItems.some(
          item => item.isHeading === true || item.isSubHeading === true
        );
        if (hasStructureRows) {
          console.log('✅ Structure rows already exist, skipping insertion');
          return enrichedItems;
        }
 
        console.log('📋 Creating heading / sub-heading rows from metadata');
        const result = [];
        let currentHeading    = null;
        let currentSubHeading = null;
 
        enrichedItems.forEach((item) => {
          // ── Heading ──────────────────────────────────────────────────────
          if (item.heading && item.heading.trim() !== '' && item.heading !== currentHeading) {
            currentHeading    = item.heading;
            currentSubHeading = null; // reset sub-heading whenever heading changes
            result.push({
              FactorId:           `heading-${uuidv4()}`,
              isHeading:          true,
              isSubHeading:       false,
              heading:            currentHeading,
              subHeading:         '',
              MeasurementId:      '',
              MeasurementType:    '',
              CostMeasurementName:'',
              Code:               '',
              QuantityTypeName:   '',
              Factor: 0, Value: 0, TotalResult: 0,
              Category: '',
            });
          }
 
          // ── Sub-heading ───────────────────────────────────────────────────
          if (
            item.subHeading &&
            item.subHeading.trim() !== '' &&
            item.subHeading !== currentSubHeading
          ) {
            currentSubHeading = item.subHeading;
            result.push({
              FactorId:           `subheading-${uuidv4()}`,
              isHeading:          false,
              isSubHeading:       true,
              heading:            currentHeading || '',
              subHeading:         currentSubHeading,
              MeasurementId:      '',
              MeasurementType:    '',
              CostMeasurementName:'',
              Code:               '',
              QuantityTypeName:   '',
              Factor: 0, Value: 0, TotalResult: 0,
              Category: '',
            });
          }
 
          result.push(item);
        });
 
        return result;
      };

      // ✅ Live links: re-read linked rows against current sibling quantities across all
      // sections, recomputing dependent factors before rendering.
      const allEnriched = [...enrichedCarbon, ...enrichedBiogenic, ...enrichedWeight, ...enrichedCost];
      resolveLineItemLinks(allEnriched, {
        rowsOf: (item) => updatedRowHistory[item.FactorId],
        setRows: (item, rows) => { updatedRowHistory[item.FactorId] = rows; },
        setQuantity: (item, qty) => {
          item.Factor = qty;
          item.TotalResult = qty * (parseFloat(item.Value) || 0);
        },
      });

      // 9️⃣ Set all state ONCE
      setCarbonLineItems(insertHeadingRowsIfNeeded(enrichedCarbon));
      setBiogenicLineItems(insertHeadingRowsIfNeeded(enrichedBiogenic));
      setWeightLineItems(insertHeadingRowsIfNeeded(enrichedWeight));
      setCostLineItems(insertHeadingRowsIfNeeded(enrichedCost));
      setRowHistory(updatedRowHistory);
      
      // 🔟 Update material name/description
      const currentMaterial = materials.find(m => String(m.Id) === String(materialId));
      if (currentMaterial) {
        setOriginalMaterialName(currentMaterial.Name);
        setNewMaterialName(currentMaterial.Name);
        setOriginalMaterialDescription(currentMaterial.Description || '');
        setNewMaterialDescription(currentMaterial.Description || '');
        setOriginalMaterialCategoryId(currentMaterial.CategoryId || '');
      }
      
      setSuccess(`Material data loaded successfully for ${regionName}`);
    } catch (error) {
      console.error('Error fetching breakdown data:', error);
      // ✅ Check for rate limit
      if (handleRateLimitError(error, () => loadBreakdownData(materialId))) return;
      showErrorModal('Failed to load breakdown data: ' + (error?.message || String(error)));
    } finally {
      setLoading(false);
    }
  },
  [
    selectedRegion,
    carbonCategories,
    biogenicCategories,
    weightCategories,
    costCategories,
    measurementIdFor,
    regionName,
    materials,
    getQuantityTypeName, // ✅ NEW
    quantityTypes 
  ]
);



  // Comparison logic
  const getComparisonData = () => {
    const allCurrentItems = [
      ...carbonLineItems,
      ...biogenicLineItems,
      ...weightLineItems,
      ...costLineItems,
    ];

    return originalData.map((originalItem) => {
      const currentItem = allCurrentItems.find((item) => 
        item.FactorId === originalItem.FactorId && 
        item.MeasurementType === originalItem.MeasurementType
      );

      return {
        original: originalItem,
        current: currentItem || {
          MeasurementId: 'DELETED',
          MeasurementType: 'DELETED',
          CostMeasurementName: 'DELETED',
          Code: 'DELETED',
          QuantityTypeName: 'DELETED',
          Value: 'DELETED',
          Factor: 'DELETED',
          TotalResult: 'DELETED',
        }
      };
    });
  };

  const openRValueCalculatorModal = () => {
    setRValueModalOpen(true);
  };

  // Handle R-Value category change
  const handleRValueCategoryChange = async (newCategory) => {
    if (!selectedMaterial || !rValueData?.Id) {
      showErrorModal('Please save an R-Value first before setting a category');
      setEditingCategory(false);
      return;
    }

    try {
      setLoading(true);
      console.log('💾 Saving category:', {
        rValueId: rValueData.Id,
        newCategory,
        oldCategory: rValueCategory
      });

      const response = await api.put(
        `/materialeditor/update-rvalue-category/${rValueData.Id}`,
        { category: newCategory }
      );

      if (response.data.success) {
        setRValueCategory(newCategory);
        setRValueData(prev => ({ 
          ...prev, 
          category: newCategory 
        }));
        setEditingCategory(false);
        
        console.log('✅ Category updated successfully:', newCategory);
        
        setSuccess('R-Value category updated successfully');
        showModal('Category Updated', `R-Value category updated to "${newCategory}" for ${regionName}.`, '🏷️');
        
        logAction(
          auth.currentUser?.uid || 'unknown',
          auth.currentUser?.email || 'unknown',
          'UPDATE_RVALUE_CATEGORY',
          {
            rValueId: rValueData.Id,
            materialId: selectedMaterial,
            newCategory,
            regionId: selectedRegion,
            regionName,
          }
        );
      } else {
        showErrorModal('Failed to update R-Value category.');
        setEditingCategory(false);
      }
    } catch (error) {
      console.error('Error updating R-Value category:', error);
      // ✅ Check for rate limit
      if (handleRateLimitError(error, () => handleRValueCategoryChange(newCategory))) return;
      showErrorModal('An error occurred while updating the R-Value category.');
      setEditingCategory(false);
    } finally {
      setLoading(false);
    }
  };

  // Download R-Value CSV
  const downloadRValueCSV = async () => {
    try {
      setLoading(true);
      
      console.log('📥 Downloading CSV for region:', selectedRegion);
      
      const response = await api.get(
        `/materialeditor/download-rvalue-csv`,
        {
          params: {
            region: selectedRegion
          },
          responseType: 'blob',
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      const timestamp = new Date().toISOString().split('T')[0];
      const regionCode = selectedRegion.toUpperCase();
      link.setAttribute('download', `quantity_unit_r_value_${regionCode}_${timestamp}.csv`);
      
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setSuccess('R-Value CSV downloaded successfully');
      showModal('Download Complete', `R-Value CSV file downloaded successfully for ${regionName}.`, '📥');

      logAction(
        auth.currentUser?.uid || 'unknown',
        auth.currentUser?.email || 'unknown',
        'DOWNLOAD_RVALUE_CSV',
        {
          regionId: selectedRegion,
          regionName,
          filename: `quantity_unit_r_value_${regionCode}_${timestamp}.csv`
        }
      );
    } catch (error) {
      console.error('❌ Error downloading R-Value CSV:', error);
      // ✅ Check for rate limit
      if (handleRateLimitError(error, downloadRValueCSV)) return;
      showErrorModal('Failed to download R-Value CSV: ' + (error?.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const saveRValueFromModal = async (calculatedQuantity, updatedRows) => {
    console.log('💾 Saving R-Value with calculator data:', {
      calculatedQuantity,
      rowsCount: updatedRows.length
    });
    
    try {
      const rValueData = {
        quantityUnitId: selectedMaterial,
        rValue: calculatedQuantity,
        calculatorRows: updatedRows || [],
        region: selectedRegion || 'vquest',
        regionId: selectedRegion || 'vquest',
        category: rValueCategory || ''
      };

      console.log('🔍 R-Value data being sent to API:', rValueData);

      const response = await saveRValue(rValueData);
      
      if (response.success) {
        setRValueData({
          Id: response.data?.id || response.id,
          DerivativeSystemId: normalizeRegionToDSID(selectedRegion),
          quantity_unit_Id: selectedMaterial,
          r_value: calculatedQuantity,
          calculator_rows: updatedRows,
          category: rValueCategory || '',
          QuantityUnitName: materials.find(m => String(m.Id) === String(selectedMaterial))?.Name || ''
        });
        
        setSuccess('R-Value and calculation breakdown saved successfully');
        showModal('R-Value Saved', `R-Value of ${calculatedQuantity} with calculation breakdown saved successfully for ${regionName}.`, '✅');
      } else {
        console.error('Failed to save R-Value:', response.error);
        showErrorModal('Failed to save R-Value: ' + (response.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error saving R-Value:', error);
      // ✅ Check for rate limit
      if (handleRateLimitError(error, () => saveRValueFromModal(calculatedQuantity, updatedRows))) return;
      showErrorModal('Failed to save R-Value: ' + (error?.message || String(error)));
    }
    
    setRValueModalOpen(false);
  };

  // R-Value file upload functions
  const handleRValueDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActiveRValue(true);
    } else if (e.type === "dragleave") {
      setDragActiveRValue(false);
    }
  };

  const handleRValueDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActiveRValue(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleRValueFiles(Array.from(e.dataTransfer.files));
    }
  };

  const handleRValueFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleRValueFiles(Array.from(e.target.files));
    }
  };

  const handleRValueFiles = async (files) => {
    if (!rValueData?.Id) {
      alert('Please save the R-Value first before uploading files');
      return;
    }

    const uploadedFiles = [];
    
    for (const file of files) {
      try {
        console.log('📤 Uploading file:', file.name);
        const response = await uploadRValueDocument(rValueData.Id, file);
        
        if (response.success) {
          console.log('✅ File uploaded:', response);
          uploadedFiles.push({
            id: response.id, // 🆕 Use database ID
            name: response.fileName,
            size: response.fileSize,
            url: response.fileUrl,
            gcpPath: response.gcpPath
          });
        }
      } catch (error) {
        console.error('❌ Upload error:', error);
        alert(`Failed to upload ${file.name}: ${error.message}`);
      }
    }

    if (uploadedFiles.length > 0) {
      setRValueFiles(prev => [...prev, ...uploadedFiles]);
      alert(`${uploadedFiles.length} file(s) uploaded successfully`);
    }
  };

  const removeRValueFile = (fileId) => {
    setRValueFiles(prev => prev.filter(file => file.id !== fileId));
  };

  const deleteRValueData = async () => {
    if (!selectedMaterial) return;
    
    if (!window.confirm('Are you sure you want to delete the R-Value data?')) {
      return;
    }

    try {
      setLoading(true);
      await deleteRValue(selectedMaterial);
      setRValueData(null);
      setRValueFiles([]);
      setRValueCategory('');
      setSuccess('R-Value deleted successfully');
      showModal('R-Value Deleted', 'R-Value data has been deleted successfully.', '🗑️');
    } catch (error) {
      console.error('Error deleting R-Value:', error);
      // ✅ Check for rate limit
      if (handleRateLimitError(error, deleteRValueData)) return;
      showErrorModal('Failed to delete R-Value: ' + (error?.message || String(error)));
    } finally {
      setLoading(false);
    }
  };

  

  // on region change: reload materials + region-scoped categories, and clear state
  useEffect(() => {
    loadMaterials();
    loadCategories();
    setSelectedMaterial('');
    setCarbonLineItems([]);
    setBiogenicLineItems([]);
    setWeightLineItems([]);
    setCostLineItems([]);
    setRowHistory({});
    setOriginalData([]);
    setOriginalMaterialName('');
    setNewMaterialName('');
    setOriginalMaterialDescription('');
    setNewMaterialDescription('');
    setRValueData(null);
    setRValueFiles([]);
    setRValueCategory('');
    setEditingCategory(false);
  }, [loadMaterials, loadCategories, selectedRegion]);

  // deep-link preselect - FIXED to support both state and URL params
  useEffect(() => {
    if (materialId && selectedRegion) {
      console.log('🔗 Deep-linking to material:', materialId);
      setSelectedMaterial(materialId);
      loadBreakdownData(materialId);
    }
  }, [materialId, selectedRegion, loadBreakdownData]);


  const handleSearchChange = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);
    const filtered = materials.filter(
      (material) =>
        material.Id.toString().toLowerCase().includes(query) ||
        material.Name.toLowerCase().includes(query)
    );
    setFilteredMaterials(filtered);
  };

  const handleMaterialChange = async (e) => {
    const materialId = e.target.value;
    setSelectedMaterial(materialId);
    setSuccess('');
    if (materialId) {
      await loadBreakdownData(materialId);
    } else {
      setCarbonLineItems([]);
      setBiogenicLineItems([]);
      setWeightLineItems([]);
      setCostLineItems([]);
      setOriginalMaterialName('');
      setNewMaterialName('');
      setOriginalMaterialDescription('');
      setNewMaterialDescription('');
      setRValueData(null);
      setRValueFiles([]);
      setRValueCategory('');
      setEditingCategory(false);
    }
  };

  // Handle Edit Name functionality
  const handleEditNameClick = () => {
    if (!selectedMaterial) {
      showErrorModal('Please select a material first');
      return;
    }
    setShowEditNameModal(true);
  };

  // ✅ Helper to build headers for direct axios calls (includes bypass token)
  const buildAxiosHeaders = (token) => {
    const headers = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      'x-region': selectedRegion,
      'x-user-id': auth.currentUser?.uid || 'unknown',
      'x-user-email': auth.currentUser?.email || 'unknown',
    };
    if (bypassToken) {
      headers['X-Rate-Limit-Bypass'] = bypassToken;
    }
    return headers;
  };

  const handleSaveName = async () => {
  if (!newMaterialName.trim()) {
    showErrorModal('Validation Error', 'Material name cannot be empty');
    return;
  }

  if (newMaterialName.trim() === originalMaterialName) {
    setShowEditNameModal(false);
    return;
  }

  try {
    setLoading(true);

    const token = await auth.currentUser?.getIdToken();

    const response = await axios.put(
      `${API_BASE_URL}/materialeditor/update-material-name/${selectedMaterial}`,
      { name: newMaterialName.trim() },
      {
        headers: buildAxiosHeaders(token),
      }
    );

    if (response.data.success) {
      setMaterials(prev => prev.map(m => 
        String(m.Id) === String(selectedMaterial) 
          ? { ...m, Name: newMaterialName.trim() }
          : m
      ));
      setFilteredMaterials(prev => prev.map(m => 
        String(m.Id) === String(selectedMaterial) 
          ? { ...m, Name: newMaterialName.trim() }
          : m
      ));
      
      setOriginalMaterialName(newMaterialName.trim());
      setShowEditNameModal(false);
      
      showModal(
        'Name Updated',
        `Material name successfully updated to "${newMaterialName.trim()}" for ${regionName}.`,
        '✏️'
      );
      setSuccess(`Material name updated successfully for ${regionName}`);

      logAction(
        auth.currentUser?.uid || 'unknown',
        auth.currentUser?.email || 'unknown',
        'UPDATE_MATERIAL_NAME',
        {
          materialId: selectedMaterial,
          oldName: originalMaterialName,
          newName: newMaterialName.trim(),
          regionId: selectedRegion,
          regionName,
        }
      );
    } else {
      showErrorModal('Update Failed', 'Failed to update material name.');
    }
  } catch (error) {
    console.error('Error updating material name:', error);
    
    // ✅ Check for rate limit
    if (handleRateLimitError(error, handleSaveName)) return;
    
    // ✅ NEW: Parse validation errors
    const validationError = parseValidationError(error);
    
    if (validationError.isValidationError) {
      const errorMessages = validationError.fields.map(f => 
        `• ${f.field}: ${f.message}\n  Your input: "${f.value}"`
      ).join('\n\n');
      
      showErrorModal(
        'Invalid Characters Detected',
        `The material name contains invalid characters:\n\n${errorMessages}\n\n` +
        `Allowed characters:\n` +
        `• Letters (A-Z, a-z)\n` +
        `• Numbers (0-9)\n` +
        `• Spaces\n` +
        `• Special characters: - _ ( ) , . / @\n\n` +
        `Please remove any other special characters and try again.`,
        '⚠️'
      );
    } else {
      showErrorModal(
        'Update Failed',
        'An error occurred while updating the material name:\n\n' +
        (error.response?.data?.message || error.message)
      );
    }
  } finally {
    setLoading(false);
  }
};

  // Save the material's building-element category. Called by the CategoryPickerModal
  // as soon as a category is picked (the picker closes itself on select). Mirrors
  // handleSaveName's persistence + error handling.
  const handleSaveCategory = async (categoryId) => {
    const nextId = categoryId || '';

    if (!selectedMaterial) {
      showErrorModal('Please select a material first');
      return;
    }

    if (nextId === (originalMaterialCategoryId || '')) {
      setShowEditCategoryModal(false);
      return;
    }

    try {
      setLoading(true);
      const token = await auth.currentUser?.getIdToken();

      const response = await axios.put(
        `${API_BASE_URL}/materialeditor/update-material-category/${selectedMaterial}`,
        { categoryId: nextId },
        { headers: buildAxiosHeaders(token) }
      );

      if (response.data.success) {
        setMaterials(prev => prev.map(m =>
          String(m.Id) === String(selectedMaterial) ? { ...m, CategoryId: nextId || null } : m
        ));
        setFilteredMaterials(prev => prev.map(m =>
          String(m.Id) === String(selectedMaterial) ? { ...m, CategoryId: nextId || null } : m
        ));
        setOriginalMaterialCategoryId(nextId);
        setShowEditCategoryModal(false);

        const catName = visibleMaterialCategories.find(c => c.id === nextId)?.name || '(none)';
        showModal(
          'Category Updated',
          `Material category successfully updated to "${catName}" for ${regionName}.`,
          '🏷️'
        );
        setSuccess(`Material category updated successfully for ${regionName}`);

        logAction(
          auth.currentUser?.uid || 'unknown',
          auth.currentUser?.email || 'unknown',
          'UPDATE_MATERIAL_CATEGORY',
          {
            materialId: selectedMaterial,
            oldCategoryId: originalMaterialCategoryId,
            newCategoryId: nextId,
            regionId: selectedRegion,
            regionName,
          }
        );
      } else {
        showErrorModal('Update Failed', 'Failed to update material category.');
      }
    } catch (error) {
      console.error('Error updating material category:', error);
      if (handleRateLimitError(error, () => handleSaveCategory(categoryId))) return;
      showErrorModal(
        'Update Failed',
        'An error occurred while updating the material category:\n\n' +
        (error.response?.data?.message || error.message)
      );
    } finally {
      setLoading(false);
    }
  };


  // Toggle the selected material between Assembly (build-up, filed by building element)
  // and Product (single EPD / Nezo EPD, building element chosen at mapping).
  const handleToggleMaterialKind = async () => {
    if (!selectedMaterial) {
      showErrorModal('Please select a material first');
      return;
    }
    const current = materials.find(m => String(m.Id) === String(selectedMaterial));
    const currentKind = current?.MaterialKind === 'Product' ? 'Product' : 'Assembly';
    const nextKind = currentKind === 'Product' ? 'Assembly' : 'Product';

    try {
      setLoading(true);
      const token = await auth.currentUser?.getIdToken();
      const response = await axios.put(
        `${API_BASE_URL}/materialeditor/update-material-kind/${selectedMaterial}`,
        { materialKind: nextKind },
        { headers: buildAxiosHeaders(token) }
      );

      if (response.data?.success) {
        setMaterials(prev => prev.map(m =>
          String(m.Id) === String(selectedMaterial) ? { ...m, MaterialKind: nextKind } : m
        ));
        setFilteredMaterials(prev => prev.map(m =>
          String(m.Id) === String(selectedMaterial) ? { ...m, MaterialKind: nextKind } : m
        ));
        showModal('Material Kind Updated', `Material is now a ${nextKind}.`, nextKind === 'Product' ? '🌿' : '🏗️');
      } else {
        showErrorModal('Update Failed', 'Failed to update material kind.');
      }
    } catch (error) {
      console.error('Error updating material kind:', error);
      if (handleRateLimitError(error, handleToggleMaterialKind)) return;
      showErrorModal('Update Failed', 'An error occurred while updating the material kind:\n\n' +
        (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  // Handle Edit Description functionality
  const handleEditDescriptionClick = () => {
    if (!selectedMaterial) {
      showErrorModal('Please select a material first');
      return;
    }
    setShowEditDescriptionModal(true);
  };

    const handleSaveDescription = async () => {
    if (newMaterialDescription.trim() === originalMaterialDescription) {
      setShowEditDescriptionModal(false);
      return;
    }

    try {
      setLoading(true);

      const token = await auth.currentUser?.getIdToken();

      const response = await axios.put(
        `${API_BASE_URL}/materialeditor/update-material-description/${selectedMaterial}`,
        { description: newMaterialDescription.trim() },
        {
          headers: buildAxiosHeaders(token),
        }
      );

      if (response.data.success) {
        setMaterials(prev => prev.map(m => 
          String(m.Id) === String(selectedMaterial) 
            ? { ...m, Description: newMaterialDescription.trim() }
            : m
        ));
        setFilteredMaterials(prev => prev.map(m => 
          String(m.Id) === String(selectedMaterial) 
            ? { ...m, Description: newMaterialDescription.trim() }
            : m
        ));
        
        setOriginalMaterialDescription(newMaterialDescription.trim());
        setShowEditDescriptionModal(false);
        
        showModal(
          'Description Updated',
          `Material description successfully updated for ${regionName}.`,
          '📝'
        );
        setSuccess(`Material description updated successfully for ${regionName}`);

        logAction(
          auth.currentUser?.uid || 'unknown',
          auth.currentUser?.email || 'unknown',
          'UPDATE_MATERIAL_DESCRIPTION',
          {
            materialId: selectedMaterial,
            oldDescription: originalMaterialDescription,
            newDescription: newMaterialDescription.trim(),
            regionId: selectedRegion,
            regionName,
          }
        );
      } else {
        showErrorModal('Update Failed', 'Failed to update material description.');
      }
    } catch (error) {
      console.error('Error updating material description:', error);
      console.error('Full error response:', error.response?.data);
      
      // ✅ Check for rate limit
      if (handleRateLimitError(error, handleSaveDescription)) return;
      
      // ✅ NEW: Parse validation errors
      const validationError = parseValidationError(error);
      
      if (validationError.isValidationError) {
        const errorMessages = validationError.fields.map(f => 
          `• ${f.field}: ${f.message}\n  Problem text: "${f.value?.substring(0, 100)}${f.value?.length > 100 ? '...' : ''}"`
        ).join('\n\n');
        
        showErrorModal(
          'Invalid Characters Detected',
          `The material description contains invalid characters:\n\n${errorMessages}\n\n` +
          `Most common issues:\n` +
          `• Unsupported special symbols\n` +
          `• Non-standard quotation marks\n` +
          `• Emojis or special Unicode characters\n\n` +
          `Allowed characters:\n` +
          `• Letters and numbers (any language)\n` +
          `• Standard punctuation: . , ! ? ; : ( ) - ' " /\n` +
          `• Common symbols: & @ # $ % * [ ] { } | < > ~ \` € £ ¥ ° © ® ™\n` +
          `• Whitespace and line breaks\n\n` +
          `Please review and remove any unusual characters.`,
          '⚠️'
        );
      } else {
        showErrorModal(
          'Update Failed',
          'An error occurred while updating the material description:\n\n' +
          (error.response?.data?.message || error.message)
        );
      }
    } finally {
      setLoading(false);
    }
  };


  // Unique option helpers
  const uniqueCategoryOptions = (cats) => {
    const seen = new Set();
    const out = [];
    for (const c of cats || []) {
      const cat = (c.Category || '').trim();
      if (!cat || seen.has(cat)) continue;
      seen.add(cat);
      out.push({ value: cat, label: cat });
    }
    return out;
  };

  const uniqueDescOptions = (cats, category) => {
    const seen = new Set();
    const out = [];
    for (const c of cats || []) {
      if (category && c.Category !== category) continue;
      const n = c?.MeasurementUnitName;
      if (!n || seen.has(n)) continue;
      seen.add(n);
      out.push({ value: n, label: n });
    }
    return out;
  };

  // Category change
  const handleLineItemCategoryChange = (index, category, setLineItems, lineItems, categories) => {
    const updated = [...lineItems];
    const item = updated[index] || {};
    let keep = false;

    if (item.CostMeasurementName) {
      const rec = categories.find(
        (c) => c.Category === category && c.MeasurementUnitName === item.CostMeasurementName
      );
      keep = !!rec;
    }

    updated[index] = {
      ...item,
      Category: category || '',
      ...(keep
        ? {}
        : {
            CostMeasurementName: '',
            Code: '',
            Value: 0,
            QuantityTypeName: '',
            TotalResult: 0,
          }),
    };

    setLineItems(updated);
  };

  // Description change
  const handleLineItemChange = (index, field, value, setLineItems, lineItems, categories) => {
    const updated = [...lineItems];

    if (field === 'description') {
      const sel = categories.find((cat) => cat.MeasurementUnitName === value);
      if (sel) {
        let base = TYPE_BASE.CO2;
        if (categories === biogenicCategories) base = TYPE_BASE.BIO;
        else if (categories === weightCategories) base = TYPE_BASE.WEIGHT;
        else if (categories === costCategories) base = TYPE_BASE.RATE;

        const regionMID = measurementIdFor(base);

        updated[index] = {
          ...updated[index],
          CostMeasurementName: sel.MeasurementUnitName || '',
          MeasurementId: regionMID,
          MeasurementType: regionMID,
          Code: sel.MeasurementUnitRateId || '',
          QuantityTypeName: sel.QuantityTypeName || '',
          Value: sel.Value || 0,
          Factor: updated[index].Factor || 0,
          TotalResult: (sel.Value || 0) * (updated[index].Factor || 0),
          FactorId: updated[index].FactorId || uuidv4(),
          Category: sel.Category || '',
        };
      } else {
        updated[index].CostMeasurementName = value;
      }
    } else if (field === 'Factor' || field === 'Value') {
      updated[index][field] = parseFloat(value) || 0;
      updated[index].TotalResult =
        (parseFloat(updated[index].Factor) || 0) * (parseFloat(updated[index].Value) || 0);
    }

    setLineItems(updated);

    logAction(auth.currentUser?.uid || 'unknown', auth.currentUser?.email || 'unknown', 'UPDATE_LINE_ITEM', {
      ...updated[index],
      regionId: selectedRegion,
      regionName,
    });
  };

  const addHeading = (setLineItems, lineItems) => {
    const newHeading = {
      FactorId: uuidv4(),
      isHeading: true,
      heading: 'New Heading',
      MeasurementId: '',
      MeasurementType: '',
      CostMeasurementName: '',
      Code: '',
      QuantityTypeName: '',
      Factor: 0,
      Value: 0,
      TotalResult: 0,
      Category: '',
    };
    
    setLineItems((prev) => [...prev, newHeading]);
  };

  const handleHeadingChange = (index, value, setLineItems, lineItems) => {
    const updatedItems = [...lineItems];
    if (updatedItems[index]) {
      updatedItems[index].heading = value;
      setLineItems(updatedItems);
    }
  };

  // 🆕 addSubHeading — inserts a sub-heading separator row
  const addSubHeading = (setLineItems, lineItems) => {
    const newSubHeading = {
      FactorId:           uuidv4(),
      isHeading:          false,
      isSubHeading:       true,
      heading:            '',
      subHeading:         'New Sub-Heading',
      MeasurementId:      '',
      MeasurementType:    '',
      CostMeasurementName:'',
      Code:               '',
      QuantityTypeName:   '',
      Factor: 0, Value: 0, TotalResult: 0,
      Category: '',
    };
    setLineItems((prev) => [...prev, newSubHeading]);
  };

  const handleSubHeadingChange = (index, value, setLineItems, lineItems) => {
    const updatedItems = [...lineItems];
    if (updatedItems[index]) {
      updatedItems[index].subHeading = value;
      setLineItems(updatedItems);
    }
  };

  const toggleEdit = (index) => {
    setEditingLineItems((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const addLineItem = (setLineItems, measurementId) => {
    const newItem = {
      FactorId: uuidv4(),
      MeasurementType: measurementId,
      MeasurementId: measurementId,
      CostMeasurementName: '',
      Code: '',
      QuantityTypeName: '',
      Factor: 0,
      Value: 0,
      TotalResult: 0,
      BreakdownData: [],
      isHeading: false,
      Category: '',
    };
    setLineItems((prev) => [...prev, newItem]);
  };

  const deleteLineItem = (index, setLineItems, lineItems) => {
    const updated = [...lineItems];
    const deletedItem = updated.splice(index, 1)[0];
    setLineItems(updated);

    setRowHistory((prevHistory) => {
      const newHistory = { ...prevHistory };
      if (deletedItem?.FactorId) delete newHistory[deletedItem.FactorId];
      return newHistory;
    });
  };

  const saveQuantityFromModal = async (calculatedQuantity, updatedRows) => {
    console.log('💾 saveQuantityFromModal called with:', {
      calculatedQuantity,
      updatedRowsCount: updatedRows.length,
      activeLineItem: activeLineItem
    });
    
    const { index, setLineItems, lineItems } = activeLineItem;
    const updated = [...lineItems];

    console.log('🔍 Current lineItems array:', lineItems.map((item, idx) => ({
      index: idx,
      isHeading: item.isHeading,
      heading: item.heading,
      FactorId: item.FactorId,
      CostMeasurementName: item.CostMeasurementName
    })));

    console.log('🔍 Current item index:', index);

    const finalQuantity = parseFloat(calculatedQuantity) || 0;
    updated[index].Factor = finalQuantity;
    updated[index].TotalResult =
      parseFloat(updated[index].Value || 0) * finalQuantity;

    const currentFactorId = updated[index].FactorId || uuidv4();
    updated[index].FactorId = currentFactorId;
    
    // Find the heading that applies to this line item
    let headingForThisItem    = null;
    let subHeadingForThisItem = null;
    for (let i = index - 1; i >= 0; i--) {
      if (lineItems[i].isHeading && !headingForThisItem) {
        headingForThisItem = lineItems[i].heading || null;
      }
      if (lineItems[i].isSubHeading && !subHeadingForThisItem) {
        subHeadingForThisItem = lineItems[i].subHeading || null;
      }
      if (headingForThisItem !== null && subHeadingForThisItem !== null) break;
    }
    
    if (!headingForThisItem) {
      console.log('   ⚠️ No heading found for this item');
    }
    
    console.log('💾 Updated line item:', {
      factorId: currentFactorId,
      factor: finalQuantity,
      value: updated[index].Value,
      totalResult: updated[index].TotalResult,
      heading: headingForThisItem
    });

    try {
      console.log('💾 Saving quantity calculator data:', {
        factorId: currentFactorId,
        materialId: selectedMaterial,
        calculatedQuantity: parseFloat(calculatedQuantity) || 0,
        rowsCount: updatedRows.length,
        heading: headingForThisItem
      });

      const response = await api.post('/line-items', {
        materialId: selectedMaterial,
        lineItems: [{
          id: currentFactorId,
          material_id: selectedMaterial,
          line_item_rows: updatedRows,
          factor: parseFloat(calculatedQuantity) || 0,
          measurement_unit_rate_id: updated[index].Code || null,
          measurement_type: updated[index].MeasurementType, 
          headings: headingForThisItem,
          sub_heading: subHeadingForThisItem, 
        }]
      });

      if (response.data?.success) {
        console.log('✅ Quantity calculator data saved successfully with heading:', headingForThisItem);
        setSuccess('Quantity calculator data saved successfully');
      } else {
        console.error('❌ Failed to save:', response.data);
        showErrorModal('Failed to save quantity calculator data');
        return;
      }

    } catch (error) {
      console.error('Error saving line item rows:', error);
      // ✅ Check for rate limit
      if (handleRateLimitError(error, () => saveQuantityFromModal(calculatedQuantity, updatedRows))) return;
      showErrorModal('Failed to save quantity calculator data: ' + (error.response?.data?.message || error.message));
    }

    // ✅ Live links: propagate this item's new quantity to same-section rows linked to it.
    const resolvedHistory = { ...rowHistory, [currentFactorId]: updatedRows };
    resolveLineItemLinks(updated, {
      rowsOf: (item) => resolvedHistory[item.FactorId],
      setRows: (item, rows) => { resolvedHistory[item.FactorId] = rows; },
      setQuantity: (item, qty) => {
        item.Factor = qty;
        item.TotalResult = qty * (parseFloat(item.Value) || 0);
      },
    });

    setRowHistory(resolvedHistory);
    setLineItems(updated);
    setModalOpen(false);
  };

  const openCalculatorModal = (index, setLineItems, lineItems) => {
    // ✅ Gather ALL line items from all sections for linking
    const allAvailableLineItems = [
      ...carbonLineItems.filter(item => !item.isHeading).map(item => ({ ...item, sectionType: 'Carbon' })),
      ...biogenicLineItems.filter(item => !item.isHeading).map(item => ({ ...item, sectionType: 'Biogenic' })),
      ...weightLineItems.filter(item => !item.isHeading).map(item => ({ ...item, sectionType: 'Weight' })),
      ...costLineItems.filter(item => !item.isHeading).map(item => ({ ...item, sectionType: 'Cost' }))
    ];

    setActiveLineItem({ 
      index, 
      setLineItems, 
      lineItems,
      allLineItems: allAvailableLineItems, // ✅ Pass all items
      sectionType: getSectionType(lineItems)
    });
    setModalOpen(true);
  };


  // ✅ NEW: Helper function to determine section type
  const getSectionType = (lineItems) => {
    if (lineItems === carbonLineItems) return 'Carbon';
    if (lineItems === biogenicLineItems) return 'Biogenic';
    if (lineItems === weightLineItems) return 'Weight';
    if (lineItems === costLineItems) return 'Cost';
    return 'Unknown';
  };

  // Add this validation function before the showComparison call
  const validateLineItems = () => {
    const allLineItems = [
      ...carbonLineItems,
      ...biogenicLineItems,
      ...weightLineItems,
      ...costLineItems,
    ];

    // Filter out heading rows
    const dataLineItems = allLineItems.filter(item => !item.isHeading && !item.isSubHeading);

    // Check for incomplete line items
    const incompleteItems = dataLineItems.filter(item => {
      const hasCategory = item.Category && item.Category.trim() !== '';
      const hasDescription = item.CostMeasurementName && item.CostMeasurementName.trim() !== '';
      const hasCode = item.Code && item.Code.trim() !== '';
      
      return !hasCategory || !hasDescription || !hasCode;
    });

    return {
      isValid: incompleteItems.length === 0,
      incompleteItems: incompleteItems
    };
  };

  const showComparison = () => {
    // ✅ Validate before showing comparison
    const validation = validateLineItems();
    
    if (!validation.isValid) {
      const itemDetails = validation.incompleteItems.map((item, idx) => {
        const issues = [];
        if (!item.Category || item.Category.trim() === '') issues.push('Category');
        if (!item.CostMeasurementName || item.CostMeasurementName.trim() === '') issues.push('Description');
        if (!item.Code || item.Code.trim() === '') issues.push('Code');
        
        return `  • Item ${idx + 1} (ID: ${item.FactorId}): Missing ${issues.join(', ')}`;
      }).join('\n');
      
      showErrorModal(
        `Cannot save material: ${validation.incompleteItems.length} line item(s) are incomplete.\n\n` +
        `Please select both Category and Description for all line items before saving:\n\n${itemDetails}\n\n` +
        `TIP: Click "Edit" on each incomplete line item and select a Category and Description from the dropdowns.`
      );
      return;
    }
    
    setShowComparisonModal(true);
  };

  // ✅ FIXED: insertMaterial with heading change detection
  const insertMaterial = async () => {
    try {
      setShowComparisonModal(false);
      setLoading(true);

      const allLineItems = [
        ...carbonLineItems,
        ...biogenicLineItems,
        ...weightLineItems,
        ...costLineItems,
      ];

      console.log('🔍 ALL LINE ITEMS BEFORE PROCESSING:', allLineItems.map((item, idx) => ({
        index: idx,
        isHeading: item.isHeading,
        heading: item.heading,
        FactorId: item.FactorId,
        CostMeasurementName: item.CostMeasurementName
      })));

      // Build new data with headings
      let currentHeading    = null;
      let currentSubHeading = null;
      const plainForUpdate  = [];
      let sortOrder = 0;
 
      allLineItems.forEach((item) => {
        if (item.isHeading) {
          currentHeading    = item.heading;
          currentSubHeading = null; // reset sub-heading when heading changes
          return;
        }
        if (item.isSubHeading) {
          currentSubHeading = item.subHeading;
          return;
        }
 
        if (!item.Code || !item.Category || !item.CostMeasurementName) {
          console.warn('⚠️ Skipping incomplete item:', item.FactorId);
          return;
        }
 
        plainForUpdate.push({
          id:                   item.FactorId || uuidv4(),
          quantityUnitId:       selectedMaterial,
          measurementUnitRateId:item.Code,
          measurementType:      item.MeasurementType,
          factor:               parseFloat(item.Factor) || 0,
          heading:              currentHeading,    // parent heading
          subHeading:           currentSubHeading, // 🆕 parent sub-heading
          sort_order:           sortOrder++,
        });
      });

      // ✅ Check if we have any valid items to save
      if (plainForUpdate.length === 0) {
        showErrorModal('No valid line items to save. Please ensure all line items have Category and Description selected.');
        setLoading(false);
        return;
      }
      
      console.log('🔄 New data with headings:', plainForUpdate.map(item => ({
        id: item.id,
        factor: item.factor,
        heading: item.heading,
        measurementUnitRateId: item.measurementUnitRateId
      })));

      // Get existing breakdown data
      const existingDataResponse = await fetchBreakdownData(selectedMaterial);
      const responseData = existingDataResponse.data?.data || existingDataResponse.data || [];
      
      if (!Array.isArray(responseData)) {
        console.error('❌ Invalid response structure:', existingDataResponse);
        showErrorModal('Error fetching existing material data - invalid format.');
        return;
      }

      console.log('🔍 Existing breakdown data:', responseData.length, 'items');

      // ✅ Get existing line items with headings
      const existingLineItemsResponse = await api.get(`/line-items/${selectedMaterial}`);
      const existingLineItems = existingLineItemsResponse.data?.data?.lineItems || [];
      
      console.log('🔍 Existing line items with headings:', existingLineItems.length, 'items');

      const existingData = responseData.map((item) => {
        // Find the heading for this item from line items data
        const lineItemData = existingLineItems.find(li => li.id === item.FactorId);
        
        return {
          id: item.FactorId,
          quantityUnitId: item.quantityUnitId || item.QuantityUnitID,
          measurementUnitRateId: item.MeasurementUnitRateId,
          factor: parseFloat(item.Factor) || 0,
          heading: lineItemData?.headings || null, // ✅ Include existing heading
        };
      });

      // Sort both arrays by id for proper comparison
      const sortById = (a, b) => (a.id || '').localeCompare(b.id || '');
      plainForUpdate.sort(sortById);
      existingData.sort(sortById);

      // ✅ IMPROVED: Compare including headings
      const hasChanges = JSON.stringify(plainForUpdate) !== JSON.stringify(existingData);
      
      console.log('🔍 Change detection:', {
        hasChanges,
        newCount: plainForUpdate.length,
        existingCount: existingData.length,
        newSample: plainForUpdate.slice(0, 2),
        existingSample: existingData.slice(0, 2)
      });

      if (!hasChanges) {
        showErrorModal('Material data already in the database. No changes detected.');
        return;
      }

      // ✅ Delete ALL existing line items first to prevent orphans
      console.log('🗑️ Deleting all existing line items to prevent orphans...');
      try {
        await api.delete(`/line-items/material/${selectedMaterial}`);
        console.log('✅ Deleted all existing line items');
      } catch (error) {
        console.error('⚠️ Error deleting existing line items:', error);
      }

      // 1) Update factors (without heading for this API)
      const plainForFactorUpdate = plainForUpdate.map(({ id, quantityUnitId, measurementUnitRateId, factor }) => ({
        id,
        quantityUnitId,
        measurementUnitRateId,
        factor,
      }));

      const originalResponse = await updateMaterialData({
        materialId: selectedMaterial,
        lineItems: plainForFactorUpdate,
      });

      if (!originalResponse.data?.success) {
        showErrorModal('Failed to insert material using updateMaterialData.');
        return;
      }

      // ✅ 2) Save line_item_rows & headings
      const formattedWithRows = plainForUpdate.map((item) => ({
        id:                       item.id,
        material_id:              selectedMaterial,
        line_item_rows:           rowHistory[item.id] || [],
        factor:                   item.factor,
        measurement_unit_rate_id: item.measurementUnitRateId || null,
        measurement_type:         item.measurementType,
        headings:                 item.heading,
        sub_heading:              item.subHeading,   // 🆕
        sort_order:               item.sort_order,
        isHeading:                false,
        isSubHeading:             false,             // 🆕
      }));

      console.log('📦 FINAL FORMATTED LINE ITEMS:', formattedWithRows.map(item => ({
        id: item.id,
        heading: item.headings,
        sortOrder: item.sort_order, 
        factor: item.factor
      })));

      try {
        const response = await api.post('/line-items', {
          materialId: selectedMaterial,
          lineItems: formattedWithRows,
        });

        if (!response.data?.success) {
          console.error('❌ Error inserting line_item_rows:', response.data);
          showErrorModal(`Error: ${response.data?.message || 'Failed to save line items'}`);
          return;
        }

        console.log('✅ Line items saved successfully with heading associations');
      } catch (error) {
        console.error('❌ Error inserting line_item_rows:', error);
        showErrorModal(`Error: ${error.response?.data?.message || error.message}`);
        return;
      }

      // Success
      showModal(
        'Material Updated',
        `Material breakdown saved successfully for ${regionName}.`,
        '🎉'
      );
      setSuccess(`Material updated successfully for ${regionName}`);

      logAction(
        auth.currentUser?.uid || 'unknown', 
        auth.currentUser?.email || 'unknown', 
        'INSERT_MATERIAL', 
        {
          materialId: selectedMaterial,
          regionId: selectedRegion,
          regionName,
          lineItemCount: formattedWithRows.length,
          headingCount: [...new Set(formattedWithRows.map(i => i.headings).filter(Boolean))].length
        }
      );

      // Refresh the page data after successful update
      setTimeout(() => {
        loadBreakdownData(selectedMaterial);
        window.location.reload();
      }, 1500);
    } catch (error) {
      console.error('Error inserting material:', error);
      // ✅ Check for rate limit
      if (handleRateLimitError(error, insertMaterial)) return;
      showErrorModal('An error occurred while inserting the material.');
    } finally {
      setLoading(false);
    }
  };

const deleteMaterial = async () => {
  if (!selectedMaterial) {
    showErrorModal('No material selected for deletion.');
    return;
  }
  
  try {
    setLoading(true);
    setShowDeleteConfirm(false); // ✅ Close confirmation modal immediately
    
    // ✅ Use the API function
    const response = await apiDeleteMaterial(selectedMaterial);

    if (response.success) {
      // ✅ Show success modal
      showModal(
        'Material Deleted',
        `Material "${originalMaterialName}" and all associated data have been deleted successfully from ${regionName}.\n\nThe page will reload in 5 seconds...`,
        '🗑️'
      );
      
      // ✅ Set success banner
      setSuccess(`Material deleted successfully for ${regionName}! Reloading...`);
      
      // ✅ Log the action
      logAction(
        auth.currentUser?.uid || 'unknown', 
        auth.currentUser?.email || 'unknown', 
        'DELETE_MATERIAL', 
        {
          materialId: selectedMaterial,
          materialName: originalMaterialName,
          regionId: selectedRegion,
          regionName,
        }
      );
      
      // ✅ Wait 5 seconds, then reload the page
      setTimeout(() => {
        window.location.reload();
      }, 5000);
      
    } else {
      showErrorModal('Failed to delete material.');
      setLoading(false);
    }
  } catch (error) {
    console.error('Error deleting material:', error);
    // ✅ Check for rate limit
    if (handleRateLimitError(error, deleteMaterial)) return;
    showErrorModal(
      'Delete Failed',
      'An error occurred while deleting the material: ' + (error.response?.data?.message || error.message)
    );
    setLoading(false);
  }
};


  const copyLineItems = () => {
    // ✅ STEP 1: Clear existing Biogenic and Weight line items
    console.log('🗑️ Clearing existing Biogenic and Weight line items...');
    setBiogenicLineItems([]);
    setWeightLineItems([]);
    
    // ✅ STEP 2: Clear related rowHistory entries
    setRowHistory((prevHistory) => {
      const newHistory = { ...prevHistory };
      
      // Remove all Biogenic line item calculator data
      biogenicLineItems.forEach((item) => {
        if (item.FactorId && newHistory[item.FactorId]) {
          delete newHistory[item.FactorId];
        }
      });
      
      // Remove all Weight line item calculator data
      weightLineItems.forEach((item) => {
        if (item.FactorId && newHistory[item.FactorId]) {
          delete newHistory[item.FactorId];
        }
      });
      
      return newHistory;
    });
    
    console.log('✅ Cleared existing data. Now copying from Carbon...');

    // ✅ NEW: Build ID mapping from Carbon to new sections
    const idMapping = {};
    
    // ✅ STEP 3: Copy from Carbon - PRESERVE ALL FIELDS
    const copiedItems = carbonLineItems.map((item) => {
      const newBiogenicId = uuidv4();
      const newWeightId = uuidv4();
      
      // Store mapping for later use
      idMapping[item.FactorId] = {
        biogenic: newBiogenicId,
        weight: newWeightId
      };
      
      return {
        ...item,
        FactorId: newBiogenicId,  // Temporary ID for biogenic
        originalFactorId: item.FactorId,
        MeasurementId: '',
        TotalResult: 0,
      };
    });

    const populateFields = (item, categories, base, targetSection) => {
      if (item.isHeading) return item;
      
      const regionMID = measurementIdFor(base);
      
      // Try to find matching category entry
      const sel = categories.find((c) => c.MeasurementUnitName === item.CostMeasurementName);

      // ✅ NEW: Remap linkedSourceId to the correct section
      let remappedLinkedSourceId = null;
      
      // Check if this item has calculator rows with a linked source
    const rows = rowHistory[item.originalFactorId];
    if (rows && rows.length > 0 && rows[0].linkedSourceId) {
      const originalLinkedId = rows[0].linkedSourceId;
      console.log(`🔗 Found linked source in copied item:`, {
        currentItemId: item.FactorId,
        originalLinkedTo: originalLinkedId,
        targetSection
      });
      
      // Remap to the corresponding ID in the target section
      if (idMapping[originalLinkedId]) {
        remappedLinkedSourceId = idMapping[originalLinkedId][targetSection];
        console.log(`   ✅ Remapped to ${targetSection}: ${remappedLinkedSourceId}`);
      }
    }
    
    if (sel) {
      return {
        ...item,
        Code: sel.MeasurementUnitRateId || item.Code,
        QuantityTypeName: sel.QuantityTypeName || item.QuantityTypeName,
        Value: sel.Value !== undefined ? sel.Value : item.Value,
        MeasurementId: regionMID,
        MeasurementType: regionMID,
        TotalResult: (sel.Value || item.Value || 0) * (item.Factor || 0),
        Category: sel.Category || item.Category || '',
        linkedSourceId: remappedLinkedSourceId, // ✅ NEW: Add remapped linked source
      };
    }
  
    return { 
      ...item, 
      MeasurementId: regionMID,
      MeasurementType: regionMID,
      Category: item.Category || '',
      TotalResult: (item.Value || 0) * (item.Factor || 0),
      linkedSourceId: remappedLinkedSourceId, // ✅ NEW: Add remapped linked source
    };
  };

  // ✅ Create Biogenic items with correct IDs from mapping
  const biogenicCopiedItems = copiedItems.map((item) => {
    const newId = idMapping[item.originalFactorId].biogenic;
    return populateFields({ ...item, FactorId: newId }, biogenicCategories, TYPE_BASE.BIO, 'biogenic');
  });
  
  // ✅ Create Weight items with correct IDs from mapping
  const weightCopiedItems = copiedItems.map((item) => {
    const newId = idMapping[item.originalFactorId].weight;
    return populateFields({ ...item, FactorId: newId }, weightCategories, TYPE_BASE.WEIGHT, 'weight');
  });

  // ✅ STEP 4: Set the new copied items (replaces cleared arrays)
  setBiogenicLineItems(biogenicCopiedItems);
  setWeightLineItems(weightCopiedItems);

  // ✅ Copy rowHistory with remapped linkedSourceId
  setRowHistory((prevHistory) => {
    const newHistory = { ...prevHistory };
    copiedItems.forEach((item, index) => {
      if (!item.isHeading) {
        const originalFactorId = item.originalFactorId;
        if (originalFactorId && prevHistory[originalFactorId]) {
          const newBiogenicFactorId = biogenicCopiedItems[index].FactorId;
          const newWeightFactorId = weightCopiedItems[index].FactorId;
          
          // ✅ Copy rows and update linkedSourceId if it exists
          const originalRows = prevHistory[originalFactorId];
          
          // Remap for Biogenic
          const biogenicRows = originalRows.map(row => {
            if (row.linkedSourceId && idMapping[row.linkedSourceId]) {
              return {
                ...row,
                linkedSourceId: idMapping[row.linkedSourceId].biogenic
              };
            }
            return { ...row };
          });
          
          // Remap for Weight
          const weightRows = originalRows.map(row => {
            if (row.linkedSourceId && idMapping[row.linkedSourceId]) {
              return {
                ...row,
                linkedSourceId: idMapping[row.linkedSourceId].weight
              };
            }
            return { ...row };
          });
          
          newHistory[newBiogenicFactorId] = biogenicRows;
          newHistory[newWeightFactorId] = weightRows;
          
          console.log(`✅ Copied calculator data from ${originalFactorId}:`, {
            biogenic: { id: newBiogenicFactorId, linkedSourceId: biogenicRows[0]?.linkedSourceId },
            weight: { id: newWeightFactorId, linkedSourceId: weightRows[0]?.linkedSourceId },
            rows: originalRows.length
          });
        }
      }
    });
    return newHistory;
  });

  console.log('✅ Copy complete with remapped links:', {
    carbonItems: carbonLineItems.length,
    biogenicCopied: biogenicCopiedItems.length,
    weightCopied: weightCopiedItems.length,
    idMappings: Object.keys(idMapping).length
  });

  setSuccess(`Copied ${copiedItems.filter(i => !i.isHeading).length} line items to Biogenic and Weight sections`);
  showModal(
    'Items Copied',
    `Successfully copied ${copiedItems.filter(i => !i.isHeading).length} line items from Carbon to Biogenic and Weight for ${regionName}.`,
    '📋'
  );
  
  logAction(auth.currentUser?.uid || 'unknown', auth.currentUser?.email || 'unknown', 'COPY_LINE_ITEMS', {
    copiedFrom: 'Carbon Line Items',
    copiedTo: ['Biogenic Line Items', 'Weight Line Items'],
    itemCount: copiedItems.filter(i => !i.isHeading).length,
    regionId: selectedRegion,
    regionName,
  });
};

  const renderRValuesSection = () => (
    <section className="section section--r-values" data-section="R-Values">
      <div className="section__head"></div>
      <h3 className="section__title">R-Values</h3>
      
      <div className="r-values-container">
        <div className="r-value-input-section">
          <div className="r-value-row">
            <div className="r-value-field">
              <label className="label">Quantity Unit</label>
              <input 
                className="input input--readonly" 
                type="text" 
                value={materials.find(m => String(m.Id) === String(selectedMaterial))?.Name || ''} 
                readOnly 
              />
            </div>

            <div className="r-value-field">
              <label className="label">Category</label>
              {editingCategory ? (
                <div style={{ minWidth: 200 }}>
                  <StyledDropdown
                    name="rValueCategory"
                    options={RVALUE_CATEGORIES.map(cat => ({ value: cat, label: cat }))}
                    value={rValueCategory}
                    onChange={(val) => {
                      console.log('📝 Category changed to:', val);
                      handleRValueCategoryChange(val);
                    }}
                    placeholder="Select Category"
                  />
                </div>
              ) : (
                <input
                  className="input input--category"
                  type="text"
                  value={rValueCategory || 'Click to select category'}
                  onClick={() => {
                    console.log('🖱️ Category field clicked, current value:', rValueCategory);
                    setEditingCategory(true);
                  }}
                  placeholder="Click to select category"
                  readOnly
                />
              )}
            </div>
            
            <div className="r-value-field">
              <label className="label">R-Value</label>
              <input
                className="input input--quantity"
                type="text"
                value={rValueData?.r_value || ''}
                onFocus={openRValueCalculatorModal}
                placeholder="Click to enter R-Value"
                readOnly
              />
            </div>
            
            <div className="r-value-actions">
              {rValueData && (
                <button
                  className="btn btn--danger btn--small"
                  onClick={deleteRValueData}
                  title="Delete R-Value"
                >
                  Delete
                </button>
              )}
            </div>
          </div>
        </div>

    <div className="r-value-upload-section">
      <h4>Supporting Documents</h4>

      <p className="section-description">
        Upload R-Value calculations, test reports, or other supporting documents
      </p>
      
      {!rValueData ? (
        <div className="upload-disabled-message">
          <p>Please save an R-Value first before uploading documents</p>
        </div>
      ) : (
        <>
          {/* NEW: Two-column wrapper */}
          <div className="r-value-columns">
            
            {/* LEFT COLUMN: Upload Area */}
            <div className="r-value-upload-column">
              <div 
                className={`file-upload-area clickable-upload ${dragActiveRValue ? 'drag-active' : ''}`}
                onDragEnter={handleRValueDrag}
                onDragLeave={handleRValueDrag}
                onDragOver={handleRValueDrag}
                onDrop={handleRValueDrop}
                onClick={() => document.getElementById('rValueFileInput').click()}
              >
                <div className="upload-content">
                  <div className="upload-icon">📁</div>
                  <p>Click here to upload R-Value documents or drag and drop</p>
                  <span className="upload-subtext">Supported formats: PDF, DOC, XLS, Images</span>
                  <input
                    id="rValueFileInput"
                    type="file"
                    multiple
                    style={{ display: 'none' }}
                    onChange={handleRValueFileInput}
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.txt"
                  />
                </div>
              </div>

              {uploadingRValue && (
                <div className="upload-progress">
                  <div className="spinner"></div>
                  <span>Uploading file...</span>
                </div>
              )}
            </div>

            {/* RIGHT COLUMN: Uploaded Files List */}
            <div className="r-value-files-column">
              {rValueFiles.length > 0 ? (
                <div className="uploaded-files">
                  <h4>Uploaded Files ({rValueFiles.length})</h4>
                  {rValueFiles.map((file) => (
                    <div key={file.id} className="file-item">
                      <div className="file-info">
                        <span className="file-name">{file.name}</span>
                        <span className="file-size">{(file.size / 1024 / 1024).toFixed(2)} MB</span>
                      </div>
                      <div className="file-actions">
                        <a 
                          href={file.url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="btn btn--small btn--secondary"
                          onClick={async (e) => {
                            // Check if URL might be expired (simple check)
                            if (file.url.includes('Expires=') || file.url.includes('X-Goog-Expires=')) {
                              e.preventDefault();
                              try {
                                console.log('🔄 Refreshing expired URL...');
                                const response = await refreshDocumentUrl(rValueData.Id, file.id);
                                if (response.success) {
                                  // Open with fresh URL
                                  window.open(response.fileUrl, '_blank');
                                  // Update state with new URL
                                  setRValueFiles(prev => prev.map(f => 
                                    f.id === file.id ? { ...f, url: response.fileUrl } : f
                                  ));
                                }
                              } catch (error) {
                                console.error('Failed to refresh URL:', error);
                                alert('Failed to access file. Please try again.');
                              }
                            }
                          }}
                        >
                          View
                        </a>
                        <button 
                          onClick={() => handleDeleteDocument(file.id)}
                          className="btn btn--danger btn--small"
                          title="Delete file"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                  <div className="no-files-message">
                    <p>No documents uploaded yet</p>
                  </div>
                )}
              </div>
              
            </div>
          </>
        )}
      </div> 
    </div> 
  </section>
);

  // Save an OC22 choice for one build-up line — immediate, decoupled from "Update Material".
  // Keyed to the CO₂ QUMURF FactorId (the row the Athena_BOM view keeps). Empty ⇒ clears.
  const handleOc22Change = async (factorId, oc22Id) => {
    setAthenaLineMappings((prev) => ({
      ...prev,
      [factorId]: {
        ...(prev[factorId] || {}),
        oc22Id: oc22Id || null,
        oc22Label: oc22Tree.find((o) => o.id === oc22Id)?.name || null,
      },
    }));
    try {
      await saveAthenaQumurfOc22({ qumurfId: factorId, oc22Id: oc22Id || null });
    } catch (e) {
      console.error('❌ Failed to save OC22 mapping:', e);
    }
  };

  // "Athena Coding" panel — USA-Canada only. Per CO₂ build-up line: Product · locked Flow/OC23
  // (read-only, from Athena_mur_oc23_map) · editable OC22 work-result dropdown. Writes
  // Athena_qumurf_oc22_map on change, which lights up the export's OC22 column. Separate panel
  // by design — it does not touch the shared build-up grid.
  const renderAthenaPanel = () => {
    const lines = (carbonLineItems || []).filter(
      (it) => !it.isHeading && !it.isSubHeading && it.FactorId
    );
    const cellStyle = { padding: '8px 10px', fontSize: 13, display: 'flex', alignItems: 'center' };
    const rowGrid = { display: 'grid', gridTemplateColumns: '1.4fr 1.4fr 1.2fr 2fr', alignItems: 'center', borderTop: '1px solid #eef2f1' };
    return (
      <div style={{ margin: '18px 0', border: '1px solid #d7e6e2', borderRadius: 10, overflow: 'visible', background: '#fff' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: '#eef5f3', borderBottom: '1px solid #d7e6e2' }}>
          <strong style={{ color: '#2f7d72' }}>Athena Coding — OC22 Work Results</strong>
          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.4, color: '#2f7d72', background: '#fff', border: '1px solid #cfe3de', padding: '2px 6px', borderRadius: 4 }}>USA-CANADA</span>
        </div>
        <div style={{ ...rowGrid, background: '#f7faf9', fontWeight: 700, fontSize: 12, color: '#527069', borderTop: 'none' }}>
          <div style={cellStyle}>Product</div>
          <div style={cellStyle}>Athena Flow 🔒</div>
          <div style={cellStyle}>OC23 Type</div>
          <div style={cellStyle}>OC22 Work Result</div>
        </div>
        {lines.length === 0 ? (
          <div style={{ padding: 14, color: '#7f8c8d', fontSize: 13 }}>No build-up lines to code yet.</div>
        ) : (
          lines.map((it) => {
            // A line is codable only once its QUMURF row is persisted — i.e. it appears in the
            // line-mappings (which read the DB). Newly-added, unsaved lines are not in there;
            // writing OC22 for them would violate the QUMURFId foreign key. Prompt to save first.
            const m = athenaLineMappings[it.FactorId];
            const saved = !!m;
            return (
              <div key={it.FactorId} style={rowGrid}>
                <div style={cellStyle}>{it.CostMeasurementName || '—'}</div>
                {!saved ? (
                  <div style={{ ...cellStyle, gridColumn: '2 / 5', color: '#b45309', fontStyle: 'italic' }}>
                    Click “Update Material” to save this line, then set its OC22 work result.
                  </div>
                ) : (
                  <>
                    <div style={{ ...cellStyle, color: m.flow ? '#243c4c' : '#b45309' }}>
                      {m.flow ? `🔒 ${m.flow}` : '⚠ no flow mapped'}
                    </div>
                    <div style={{ ...cellStyle, color: '#527069' }}>{m.flowTypeOc23 || ''}</div>
                    <div style={{ padding: '4px 10px' }}>
                      <CategoryPickerModal
                        categories={oc22Tree}
                        value={m.oc22Id || ''}
                        onChange={(val) => handleOc22Change(it.FactorId, val)}
                        placeholder="Select OC22 work result…"
                        title="Select OC 22 — Work Result"
                      />
                    </div>
                  </>
                )}
              </div>
            );
          })
        )}
      </div>
    );
  };

  const renderSection = (title, lineItems, setLineItems, categories, measurementId, collapsed, onToggle) => {
     // ✅ NEW: Calculate section total (excluding heading rows)
    const sectionTotal = lineItems
      .filter(item => !item.isHeading && !item.isSubHeading)
      .reduce((sum, item) => sum + (parseFloat(item.TotalResult) || 0), 0);
    // Drag-and-drop handlers
    const handleDragStart = (e, index) => {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/html', index.toString());
      e.currentTarget.style.opacity = '0.4';
    };

    const handleDragEnd = (e) => {
      e.currentTarget.style.opacity = '1';
    };

    const handleDragOver = (e) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      return false;
    };

    const handleDrop = (e, dropIndex) => {
      e.stopPropagation();
      e.preventDefault();

      const dragIndex = parseInt(e.dataTransfer.getData('text/html'));
      
      if (dragIndex === dropIndex) return;

      const updated = [...lineItems];
      const [draggedItem] = updated.splice(dragIndex, 1);
      updated.splice(dropIndex, 0, draggedItem);

      // Update heading associations after reorder
      let currentHeading    = null;
      let currentSubHeading = null;
      updated.forEach((item, idx) => {
        if (item.isHeading) {
          currentHeading    = item.heading;
          currentSubHeading = null;
        } else if (item.isSubHeading) {
          currentSubHeading = item.subHeading;
        } else {
          updated[idx].heading    = currentHeading;
          updated[idx].subHeading = currentSubHeading;
        }
      });
      
      setLineItems([...updated]);
      
      console.log('🔄 Reordered items - moved from', dragIndex, 'to', dropIndex);
    };

    return (
      <section className="section section--line-items" data-section={title}>
        <div className="section__head">
          {/* Add collapse toggle button */}
          {onToggle && (
          <button
            className="btn-collapse-toggle"
            onClick={onToggle}
            title={collapsed ? 'Expand section' : 'Collapse section'}
          >
            {collapsed ? '▶' : '▼'}
          </button>
        )}
        </div>
        {/* ✅ NEW: Title row with total */}
        <div className="section__title-row">
          <h3 className="section__title">{title}</h3>
          <div className="section__total">
            <span className="section__total-label">Total:</span>
            <span className="section__total-value">{sectionTotal.toFixed(2)}</span>
          </div>
        </div>

        {/* Only render content if not collapsed */}
        {!collapsed && (
        <>
        <div className="line-items-header">
          <span>⋮⋮</span>
          <span>M_ID</span>
          <span>Category</span>
          <span>Description</span>
          <span>Code</span>
          <span>Unit</span>
          <span>Quantity</span>
          <span>Value</span>
          <span>Total</span>
          <span>ID</span>
          <span>Actions</span>
        </div>

        {lineItems.map((item, index) => {
          const isIncomplete = !item.isHeading && !item.isSubHeading && (
            !item.Category          || item.Category.trim()          === '' ||
            !item.CostMeasurementName || item.CostMeasurementName.trim() === '' ||
            !item.Code              || item.Code.trim()              === ''
          );
 
          return (
            <React.Fragment key={`${item.FactorId || 'new'}-${index}`}>
 
              {/* ── HEADING ROW ─────────────────────────────────────────── */}
              {item.isHeading && (
                <div
                  className="heading-item"
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragEnd={handleDragEnd}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, index)}
                >
                  <span className="drag-handle">⋮⋮</span>
                  <input
                    className="input input--heading"
                    type="text"
                    value={item.heading || ''}
                    placeholder="Enter heading..."
                    onChange={(e) =>
                      handleHeadingChange(index, e.target.value, setLineItems, lineItems)
                    }
                  />
                  <button
                    className="btn btn--danger btn--small"
                    onClick={() => deleteLineItem(index, setLineItems, lineItems)}
                  >
                    Delete
                  </button>
                </div>
              )}
 
              {/* ── SUB-HEADING ROW 🆕 ──────────────────────────────────── */}
              {item.isSubHeading && (
                <div
                  className="subheading-item"
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragEnd={handleDragEnd}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, index)}
                >
                  <span className="drag-handle">⋮⋮</span>
                  <span className="subheading-indent">└</span>
                  <input
                    className="input input--subheading"
                    type="text"
                    value={item.subHeading || ''}
                    placeholder="Enter sub-heading..."
                    onChange={(e) =>
                      handleSubHeadingChange(index, e.target.value, setLineItems, lineItems)
                    }
                  />
                  <button
                    className="btn btn--danger btn--small"
                    onClick={() => deleteLineItem(index, setLineItems, lineItems)}
                  >
                    Delete
                  </button>
                </div>
              )}
 
              {/* ── DATA ROW ────────────────────────────────────────────── */}
              {!item.isHeading && !item.isSubHeading && (
                <div
                  className={`line-item ${isIncomplete ? 'line-item--incomplete' : ''}`}
                  data-row-index={index}
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragEnd={handleDragEnd}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, index)}
                >
                  <span className="drag-handle" style={{ cursor: 'grab' }}>⋮⋮</span>
 
                  {isIncomplete && (
                    <span className="incomplete-indicator" title="Please select Category and Description">
                      ⚠️
                    </span>
                  )}
 
                  <span
                    className="measurement-id-icon"
                    title={item.MeasurementId}
                    style={{ cursor: 'pointer', fontSize: '16px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                  >
                    ℹ️
                  </span>
 
                  {editingLineItems[index] ? (
                    <div style={{ minWidth: 200 }}>
                      <StyledDropdown
                        name={`cat-${item.FactorId || index}`}
                        options={uniqueCategoryOptions(categories)}
                        value={item.Category}
                        onChange={(val) =>
                          handleLineItemCategoryChange(index, val, setLineItems, lineItems, categories)
                        }
                        placeholder="⚠️ Select Category"
                      />
                    </div>
                  ) : (
                    <input
                      className={`input input--readonly ${!item.Category ? 'input--missing' : ''}`}
                      type="text"
                      value={item.Category || '⚠️ REQUIRED'}
                      readOnly
                    />
                  )}
 
                  {editingLineItems[index] ? (
                    <div style={{ minWidth: 260 }}>
                      <StyledDropdown
                        name={`desc-${item.FactorId || index}`}
                        options={uniqueDescOptions(categories, item.Category)}
                        value={item.CostMeasurementName}
                        onChange={(val) =>
                          handleLineItemChange(index, 'description', val, setLineItems, lineItems, categories)
                        }
                        placeholder="Select Description"
                      />
                    </div>
                  ) : (
                    <div className="description-cell-wrapper" data-tooltip={item.CostMeasurementName || ''}>
                      <input
                        className={`input input--readonly ${!item.CostMeasurementName ? 'input--missing' : ''}`}
                        type="text"
                        value={item.CostMeasurementName || '⚠️ REQUIRED'}
                        readOnly
                      />
                    </div>
                  )}
 
                  <input className="input input--readonly" type="text" value={item.Code}          readOnly />
                  <input className="input input--readonly" type="text" value={item.QuantityTypeName} readOnly />
 
                  <input
                    className="input input--quantity"
                    type="text"
                    value={item.Factor}
                    onFocus={() => openCalculatorModal(index, setLineItems, lineItems)}
                    readOnly
                  />
 
                  <input
                    className="input input--value"
                    type="number"
                    value={item.Value}
                    onChange={(e) => handleLineItemChange(index, 'Value', e.target.value, setLineItems, lineItems)}
                  />
 
                  <input className="input input--readonly" type="number" value={item.TotalResult} readOnly />
                  <input className="input input--readonly" type="text"   value={item.FactorId}    readOnly />
 
                  <div className="btn-group btn-group--row-actions">
                    <button
                      className="btn btn--primary btn--edit-save"
                      onClick={() => toggleEdit(index)}
                      aria-label={editingLineItems[index] ? 'Save row' : 'Edit row'}
                    >
                      {editingLineItems[index] ? 'Save' : 'Edit'}
                    </button>
                    <button
                      className="btn btn--danger btn--delete-row"
                      onClick={() => deleteLineItem(index, setLineItems, lineItems)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              )}
 
            </React.Fragment>
          );
        })}
 
        {/* ── Section action buttons ──────────────────────────────────────── */}
        <div className="section__actions">
          <button className="btn btn--success btn--add-line"
            onClick={() => addLineItem(setLineItems, measurementId)}>
            + Add Line Item
          </button>
          <button className="btn btn--indigo btn--add-heading"
            onClick={() => addHeading(setLineItems, lineItems)}>
            + Add Heading
          </button>
          {/* 🆕 Sub-heading button */}
          <button className="btn btn--teal btn--add-subheading"
            onClick={() => addSubHeading(setLineItems, lineItems)}>
            + Add Sub-Heading
          </button>
        </div>
        </>
              )}
      
      {/* Show collapsed message */}
      {collapsed && (
        <div className="section-collapsed-message">
          <p>Section collapsed - Click ▶ to expand ({lineItems.filter(i => !i.isHeading).length} items)</p>
        </div>
      )}
      </section>
    );
  };

  // banners auto-dismiss
  useEffect(() => {
    if (success) {
      const t = setTimeout(() => setSuccess(''), 5000);
      return () => clearTimeout(t);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const t = setTimeout(() => setError(''), 8000);
      return () => clearTimeout(t);
    }
  }, [error]);
  

  // lock scroll while any modal is open — ✅ UPDATED: includes showBypassModal
  useEffect(() => {
    const anyOpen = showDeleteConfirm || showComparisonModal || modalOpen || modal.open || errorModal.open || showEditNameModal || showEditDescriptionModal || rValueModalOpen || showBypassModal;
    document.documentElement.classList.toggle('vq-modal-open', anyOpen);
    document.body.classList.toggle('vq-modal-open', anyOpen);
    return () => {
      document.documentElement.classList.remove('vq-modal-open');
      document.body.classList.remove('vq-modal-open');
    };
  }, [showDeleteConfirm, showComparisonModal, modalOpen, modal.open, errorModal.open, showEditNameModal, showEditDescriptionModal, rValueModalOpen, showBypassModal]);

  if (loading) {
    return (
      <div className="supplier-dashboard-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading Material Data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="rate-editor-page">
 
      {/* ── Sticky header ── */}
      <div className="me-sticky-header">
        <h1 className="page-title">Material Editor</h1>
        <div className="region-info">
          <span className="badge badge--region">Region: {regionName}</span>
          {selectedMaterial && (
            <span className="badge badge--material">
              Material ID: {selectedMaterial}
            </span>
          )}
          {selectedMaterial && (() => {
            const mat = materials.find(m => String(m.Id) === String(selectedMaterial));
            return mat?.Name ? (
              <span className="badge badge--name" title={mat.Name}>
                📋 {mat.Name}
              </span>
            ) : null;
          })()}
          {selectedMaterial && (() => {
            const mat = materials.find(m => String(m.Id) === String(selectedMaterial));
            const unit = mat?.QuantityTypeName;
            return unit ? (
              <span className="badge badge--unit">
                📐 {unit}
              </span>
            ) : null;
          })()}
          {selectedMaterial && (() => {
            const mat = materials.find(m => String(m.Id) === String(selectedMaterial));
            const kind = mat?.MaterialKind === 'Product' ? 'Product' : 'Assembly';
            return (
              <span className="badge badge--kind">
                {kind === 'Product' ? '🌿 Product / Nezo EPD' : '🏗️ Assembly'}
              </span>
            );
          })()}
        </div>
      </div>

      {error && (
        <div className="alert alert--error" role="alert">
          <span className="alert__icon" aria-hidden>⚠️</span>
          <span className="alert__text">{error}</span>
          <button className="alert__close" onClick={() => showErrorModal('')} aria-label="Dismiss error">
            ×
          </button>
        </div>
      )}
      {success && (
        <div className="alert alert--success" role="status">
          <span className="alert__icon" aria-hidden>✅</span>
          <span className="alert__text">{success}</span>
          <button className="alert__close" onClick={() => setSuccess('')} aria-label="Dismiss message">
            ×
          </button>
        </div>
      )}

      <div className="form-container form-container--material">
        <label className="label" htmlFor="materialSearch">
          Search Material
        </label>
        <input
          className="input input--search"
          type="text"
          id="materialSearch"
          placeholder="Search by ID or Name"
          value={searchQuery}
          onChange={handleSearchChange}
        />

        <label className="label" htmlFor="materialSelect">
          Select Material
        </label>
        <div style={{ maxWidth: 480 }}>
          <StyledDropdown
            name="materialSelect"
            options={filteredMaterials.map((m) => ({
              value: m.Id,
              label: `${m.Name} (ID: ${m.Id})`,
            }))}
            value={selectedMaterial}
            onChange={(val) => handleMaterialChange({ target: { value: val } })}
            placeholder="Select Material"
          />
        </div>

        <small className="hint">
          {filteredMaterials.length
            ? `${filteredMaterials.length} materials available for ${regionName}`
            : 'No materials found'}
        </small>
      </div>

      <div className="material-actions">
        <button
          onClick={copyLineItems}
          className="btn btn--copy copy-line-items-button"
          disabled={carbonLineItems.length === 0}
        >
          Copy to Biogenic &amp; Weight
        </button>

        <button
          onClick={handleEditNameClick}
          className="btn btn--edit-name edit-name-button"
          disabled={!selectedMaterial}
          title={!selectedMaterial ? "Please select a material first" : "Edit material name"}
        >
          ✏️ Edit Name
        </button>

        {(() => {
          const mat = materials.find(m => String(m.Id) === String(selectedMaterial));
          const kind = mat?.MaterialKind === 'Product' ? 'Product' : 'Assembly';
          const nextKind = kind === 'Product' ? 'Assembly' : 'Product';
          return (
            <button
              onClick={handleToggleMaterialKind}
              className="btn material-kind-button"
              disabled={!selectedMaterial || loading}
              title={!selectedMaterial ? "Please select a material first" : `Switch this material to ${nextKind}`}
            >
              {kind === 'Product' ? '🏗️ Mark as Assembly' : '🌿 Mark as Product'}
            </button>
          );
        })()}

        <button
          onClick={handleEditDescriptionClick}
          className="btn btn--edit-description edit-description-button"
          disabled={!selectedMaterial}
          title={!selectedMaterial ? "Please select a material first" : "Edit material description"}
        >
          📝 Edit Description
        </button>
        <button
          onClick={() => {
            if (!selectedMaterial) { showErrorModal('Please select a material first'); return; }
            setShowEditCategoryModal(true);
          }}
          className="btn edit-category-button"
          disabled={!selectedMaterial}
          title={!selectedMaterial ? "Please select a material first" : "Edit building element category"}
        >
          🏷️ Edit Category
        </button>
        <button onClick={downloadRValueCSV} className="btn btn--download download-button btn--secondary-big">
          📥 Download R-Value CSV
        </button>
      </div>

      {selectedMaterial && renderRValuesSection()}

      {renderSection(
        'Carbon Line Items',
        carbonLineItems,
        setCarbonLineItems,
        carbonCategories,
        measurementIdFor(TYPE_BASE.CO2),
        false, // never collapsed
        null  // no toggle handler
      )}

      {/* Athena OC22 authoring — USA-Canada only; keyed to the CO₂ build-up lines above. */}
      {isAthenaRegion && selectedMaterial && renderAthenaPanel()}
      {renderSection(
        'Biogenic Line Items',
        biogenicLineItems,
        setBiogenicLineItems,
        biogenicCategories,
        measurementIdFor(TYPE_BASE.BIO),
        biogenicCollapsed,
        () => setBiogenicCollapsed(!biogenicCollapsed)
      )}
      {renderSection(
        'Weight Line Items',
        weightLineItems,
        setWeightLineItems,
        weightCategories,
        measurementIdFor(TYPE_BASE.WEIGHT),
        weightCollapsed,
        () => setWeightCollapsed(!weightCollapsed)
      )}
      {renderSection(
        'Cost Line Items',
        costLineItems,
        setCostLineItems,
        costCategories,
        measurementIdFor(TYPE_BASE.RATE),
        false, // never collapsed
        null  // no toggle handler
      )}

      <QuantityCalculatorModal
        isOpen={rValueModalOpen}
        onClose={() => setRValueModalOpen(false)}
        onSave={(calculatedQuantity, updatedRows) => saveRValueFromModal(calculatedQuantity, updatedRows)}
        initialRows={rValueData?.calculator_rows || []} 
        title="R-Value Calculator"
        lineItems={activeLineItem?.allLineItems || []}
        sectionType="R-Value"
      />

      <QuantityCalculatorModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={(calculatedQuantity, updatedRows) => saveQuantityFromModal(calculatedQuantity, updatedRows)}
        initialRows={rowHistory[activeLineItem?.lineItems?.[activeLineItem?.index]?.FactorId] || []}
        lineItems={activeLineItem?.lineItems || []} // ✅ NEW: Pass current section's line items
        sectionType={activeLineItem?.sectionType || ''} // ✅ NEW: Pass section type
        currentItemId={activeLineItem?.lineItems?.[activeLineItem?.index]?.FactorId}
        lineItemRowsById={rowHistory}
        activeLineItem={activeLineItem}
        setRowHistory={setRowHistory}
      />

      <div className="control-buttons">
        <button
          onClick={copyLineItems}
          className="btn btn--copy copy-line-items-button"
          disabled={carbonLineItems.length === 0}
        >
          Copy to Biogenic &amp; Weight
        </button>
        <button onClick={showComparison} className="btn insert-button btn--primary-big">
          Update Material Data
        </button>
        <button onClick={() => setShowDeleteConfirm(true)} className="btn delete-button btn--danger-big">
          Delete Material
        </button>
      </div>

      {showEditNameModal && (
        <VQModal aria-label="Edit material name">
          <h2 className="modal__title">Edit Material Name</h2>
          <div className="modal__content">
            <div className="form-group">
              <label className="label" htmlFor="newMaterialName">
                Material Name
              </label>
              <input
                className="input"
                type="text"
                id="newMaterialName"
                value={newMaterialName}
                onChange={(e) => setNewMaterialName(e.target.value)}
                placeholder="Enter new material name"
                maxLength={255}
              />
              <small className="hint">
                Current name: {originalMaterialName}
              </small>
            </div>
          </div>
          <div className="modal__actions">
            <button 
              className="btn btn--success" 
              onClick={handleSaveName} 
              disabled={loading || !newMaterialName.trim()}
            >
              {loading ? 'Saving...' : 'Save Name'}
            </button>
            <button 
              className="btn btn--secondary" 
              onClick={() => {
                setShowEditNameModal(false);
                setNewMaterialName(originalMaterialName);
              }} 
              disabled={loading}
            >
              Cancel
            </button>
          </div>
        </VQModal>
      )}

      {/* Edit Category — reuses the building-element picker from the New Material page.
          hideTrigger + controlled open lets the "Edit Category" button open it directly. */}
      <CategoryPickerModal
        hideTrigger
        open={showEditCategoryModal}
        onOpenChange={setShowEditCategoryModal}
        categories={visibleMaterialCategories}
        value={originalMaterialCategoryId}
        onChange={handleSaveCategory}
        title="Select Building Element Category"
      />

      {showEditDescriptionModal && (
        <VQModal aria-label="Edit material description">
          <h2 className="modal__title">Edit Material Description</h2>
          <div className="modal__content">
            <div className="form-group">
              <label className="label" htmlFor="materialNameDisplay">
                Material Name
              </label>
              <input
                className="input input--readonly"
                type="text"
                id="materialNameDisplay"
                value={materials.find(m => String(m.Id) === String(selectedMaterial))?.Name || ''}
                readOnly
              />
            </div>
            <div className="form-group">
              <label className="label" htmlFor="originalDescription">
                Current Description
              </label>
              <textarea
                className="input textarea--readonly"
                id="originalDescription"
                value={originalMaterialDescription || 'No description currently set'}
                readOnly
                rows="3"
              />
            </div>
            <div className="form-group">
              <label className="label" htmlFor="newMaterialDescription">
                New Description
              </label>
              <textarea
                className="input"
                id="newMaterialDescription"
                value={newMaterialDescription}
                onChange={(e) => setNewMaterialDescription(e.target.value)}
                placeholder="Enter material description"
                rows="4"
                maxLength={1000}
              />
              <small className="hint">
                {newMaterialDescription.length}/1000 characters
              </small>
            </div>
          </div>
          <div className="modal__actions">
            <button 
              className="btn btn--success" 
              onClick={handleSaveDescription} 
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save Description'}
            </button>
            <button 
              className="btn btn--secondary" 
              onClick={() => {
                setShowEditDescriptionModal(false);
                setNewMaterialDescription(originalMaterialDescription);
              }} 
              disabled={loading}
            >
              Cancel
            </button>
          </div>
        </VQModal>
      )}

      {showDeleteConfirm && (
        <VQModal aria-label="Confirm deletion">
          <h2 className="modal__title">Confirm Deletion</h2>
          <p className="modal__text">
            Are you sure you want to delete this material from {regionName}? This action is irreversible.
          </p>
          <div className="modal__actions">
            <button className="btn btn--danger" onClick={deleteMaterial} disabled={loading}>
              {loading ? 'Deleting...' : 'Yes, Delete'}
            </button>
            <button className="btn btn--secondary" onClick={() => setShowDeleteConfirm(false)} disabled={loading}>
              Cancel
            </button>
          </div>
        </VQModal>
      )}

      {showComparisonModal && (
        <VQModal wide aria-label="Compare changes">
          <h2 className="modal__title">Compare Changes - {regionName}</h2>

          <div className="comparison-table">
            <div className="table-header">
              <span>Measurement ID (Before)</span>
              <span>Measurement ID (After)</span>
              <span>Description (Before)</span>
              <span>Description (After)</span>
              <span>Code (Before)</span>
              <span>Code (After)</span>
              <span>Unit (Before)</span>
              <span>Unit (After)</span>
              <span>Value (Before)</span>
              <span>Value (After)</span>
              <span>Factor (Before)</span>
              <span>Factor (After)</span>
              <span>Total (Before)</span>
              <span>Total (After)</span>
            </div>

            {getComparisonData().map(({ original, current }, index) => (
              <div className={`table-row ${index % 2 === 0 ? 'even-row' : 'odd-row'}`} key={`${original.FactorId}-${index}`}>
                <span>{original.MeasurementType}</span>
                <span className={original.MeasurementType !== current.MeasurementType ? 'diff' : ''}>
                  {current.MeasurementType || '-'}
                </span>
                <span>{original.CostMeasurementName}</span>
                <span className={original.CostMeasurementName !== current.CostMeasurementName ? 'diff' : ''}>
                  {current.CostMeasurementName || '-'}
                </span>
                <span>{original.MeasurementUnitRateId || '-'}</span>
                <span className={(original.MeasurementUnitRateId || '-') !== (current.Code || '-') ? 'diff' : ''}>
                  {current.Code || '-'}
                </span>
                <span>{original.QuantityTypeName}</span>
                <span className={original.QuantityTypeName !== current.QuantityTypeName ? 'diff' : ''}>
                  {current.QuantityTypeName || '-'}
                </span>
                <span>{original.Value}</span>
                <span className={parseFloat(original.Value) !== parseFloat(current.Value) ? 'diff' : ''}>
                  {current.Value || '-'}
                </span>
                <span>{original.Factor}</span>
                <span className={parseFloat(original.Factor) !== parseFloat(current.Factor) ? 'diff' : ''}>
                  {current.Factor || '-'}
                </span>
                <span>{original.TotalResult}</span>
                <span className={parseFloat(original.TotalResult) !== parseFloat(current.TotalResult) ? 'diff' : ''}>
                  {current.TotalResult || '-'}
                </span>
              </div>
            ))}
          </div>

          <div className="modal-actions">
            <button className="btn proceed-button btn--success" onClick={insertMaterial} disabled={loading}>
              {loading ? 'Updating...' : 'Proceed with Update'}
            </button>
            <button
              className="btn cancel-button btn--secondary"
              onClick={() => setShowComparisonModal(false)}
              disabled={loading}
            >
              Cancel
            </button>
          </div>
        </VQModal>
      )}

      {/* ✅ Rate Limit Bypass Modal */}
      {showBypassModal && (
        <div className="success-modal-overlay" onClick={() => setShowBypassModal(false)}>
          <div className="success-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="success-modal-header">
              <div className="success-modal-icon">🔐</div>
              <h2>Rate Limit Reached</h2>
            </div>
            <div className="success-modal-body">
              <p>Too many requests. Enter your password to continue:</p>
              <input
                type="password"
                className="input"
                placeholder="Enter your password"
                value={bypassPassword}
                onChange={(e) => setBypassPassword(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleBypass()}
                style={{ marginTop: '12px', width: '100%' }}
                autoFocus
              />
              {bypassError && (
                <p style={{ color: '#e74c3c', marginTop: '8px', fontSize: '14px' }}>
                  {bypassError}
                </p>
              )}
            </div>
            <div className="success-modal-footer" style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
              <button
                className="btn btn--secondary"
                onClick={() => {
                  setShowBypassModal(false);
                  setBypassPassword('');
                  setBypassError('');
                }}
              >
                Cancel
              </button>
              <button
                className="success-modal-button"
                onClick={handleBypass}
                disabled={bypassLoading}
              >
                {bypassLoading ? 'Verifying...' : 'Verify & Continue'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ErrorModal
        isOpen={errorModal.open}
        onClose={() => setErrorModal((m) => ({ ...m, open: false }))}
        title={errorModal.title}
        message={errorModal.message}
        icon={errorModal.icon}
      />

      <SuccessModal
        isOpen={modal.open}
        onClose={() => setModal((m) => ({ ...m, open: false }))}
        title={modal.title}
        message={modal.message}
        icon={modal.icon}
      />
    </div>
  );
}

export default MaterialEditorPage;